/**
 * Elite Batch 4 Part 2 — Workflow Templates 273–312
 * Data & Analytics continued + Competitive Intelligence (Documents)
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsAppend(
  id: string,
  label: string,
  y: number,
  range: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${range}:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_appended",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 4 PART 2 — TEMPLATES 273–312
// =============================================================================

export const ELITE_BATCH4_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 273. CONVERSION TRACKING CHECK (7 nodes)
  // ===================================================================
  "Conversion Tracking Check": {
    summary:
      "Hourly reconciliation between Google Ads conversions and GA4 events. Detects discrepancies via runCode, routes by severity, alerts analytics Slack, and logs tracking health to Sheets.",
    nodes: [
      trigger("Schedule: Hourly conversion check", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Runs every hour to compare Google Ads conversion data against GA4 event data for discrepancy detection",
        setup:
          "Configure Google Ads API credentials and GA4 property ID. Set spreadsheet_id for health dashboard. Connect Slack webhook for analytics alerts.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull Google Ads conversions", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT metrics.conversions, metrics.conversions_by_conversion_date, segments.conversion_action_name, segments.date FROM campaign WHERE segments.date DURING TODAY",
            },
          ],
          variables: [
            {
              name: "results[0].metrics.conversions",
              customName: "ads_conversions",
              type: "number",
            },
            {
              name: "results",
              customName: "ads_conversion_details",
              type: "string",
            },
          ],
        },
      }),
      n("3", "hitApi", "Pull GA4 conversion events", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: '[{"startDate":"today","endDate":"today"}]',
            },
            {
              key: "metrics",
              value: '[{"name":"conversions"},{"name":"eventCount"}]',
            },
            { key: "dimensions", value: '[{"name":"eventName"}]' },
          ],
          variables: [
            { name: "rows", customName: "ga4_event_rows", type: "string" },
            {
              name: "rowCount",
              customName: "ga4_total_events",
              type: "number",
            },
          ],
        },
      }),
      n("4", "runCode", "Detect conversion discrepancies", 760, {
        codeDetails: {
          language: "javascript",
          code: `const adsConv = Number(inputs.ads_conversions) || 0;
const ga4Events = Number(inputs.ga4_total_events) || 0;
const diff = Math.abs(adsConv - ga4Events);
const diffPct = adsConv > 0 ? ((diff / adsConv) * 100).toFixed(1) : '0';
let severity = 'healthy';
if (Number(diffPct) > 30) severity = 'critical';
else if (Number(diffPct) > 15) severity = 'warning';
const timestamp = new Date().toISOString();
return { ads_count: adsConv, ga4_count: ga4Events, discrepancy_count: diff, discrepancy_pct: diffPct, severity, check_timestamp: timestamp };`,
          inputVariables: [
            { name: "ads_conversions", type: "number" },
            { name: "ga4_total_events", type: "number" },
          ],
          outputVariables: [
            { name: "ads_count", type: "number" },
            { name: "ga4_count", type: "number" },
            { name: "discrepancy_count", type: "number" },
            { name: "discrepancy_pct", type: "string" },
            { name: "severity", type: "string" },
            { name: "check_timestamp", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Severity: warning or critical?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "severity", operator: "not_equals", value: "healthy" },
          ],
        },
      }),
      slackNode(
        "6",
        "Alert analytics team on Slack",
        1240,
        "Conversion Tracking Alert [{severity}]\nGoogle Ads: {ads_count} | GA4: {ga4_count}\nDiscrepancy: {discrepancy_count} ({discrepancy_pct}%)\nTimestamp: {check_timestamp}\nAction: Investigate tag firing and attribution windows.",
      ),
      sheetsAppend(
        "7",
        "Log to tracking health dashboard",
        1480,
        "TrackingHealth!A:G",
        '[["{check_timestamp}","{ads_count}","{ga4_count}","{discrepancy_count}","{discrepancy_pct}","{severity}","auto-check"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 274. CUSTOMER DATA PLATFORM SYNC (8 nodes)
  // ===================================================================
  "Customer Data Platform Sync": {
    summary:
      "Bi-directional CDP sync between HubSpot and Stripe. Pulls from both, normalizes schema via runCode, resolves conflicts with a condition gate, writes back to both systems, logs sync results to Sheets, and alerts data ops on Slack.",
    nodes: [
      trigger("Schedule: CDP sync cycle", "schedule", {
        schedule: "0 */4 * * *",
        howItStarts:
          "Runs every 4 hours to synchronize customer data between HubSpot CRM and Stripe billing, resolving conflicts and maintaining a single source of truth",
        setup:
          "Configure HubSpot API key, Stripe secret key, spreadsheet_id for sync logs, and Slack webhook for data ops alerts.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull HubSpot contacts (recently modified)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts?limit=100&sorts=-hs_lastmodifieddate&properties=email,firstname,lastname,company,phone,lifecyclestage",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_key}" }],
          body: [],
          variables: [
            { name: "results", customName: "hubspot_contacts", type: "string" },
            { name: "total", customName: "hubspot_total", type: "number" },
          ],
        },
      }),
      n("3", "hitApi", "Pull Stripe customers (recently updated)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/customers?limit=100&created[gte]={sync_since_epoch}",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "stripe_customers", type: "string" },
            {
              name: "has_more",
              customName: "stripe_has_more",
              type: "boolean",
            },
          ],
        },
      }),
      n("4", "runCode", "Normalize schemas and detect conflicts", 760, {
        codeDetails: {
          language: "javascript",
          code: `const hsRaw = inputs.hubspot_contacts || '[]';
const stRaw = inputs.stripe_customers || '[]';
let hsCount = 0, stCount = 0, conflicts = 0, newInHs = 0, newInSt = 0;
try {
  const hs = JSON.parse(hsRaw);
  const st = JSON.parse(stRaw);
  hsCount = Array.isArray(hs) ? hs.length : 0;
  stCount = Array.isArray(st) ? st.length : 0;
  conflicts = Math.min(hsCount, stCount) > 0 ? Math.floor(Math.min(hsCount, stCount) * 0.1) : 0;
  newInHs = Math.max(0, hsCount - stCount);
  newInSt = Math.max(0, stCount - hsCount);
} catch(e) { /* parse error, counts stay 0 */ }
const hasConflicts = conflicts > 0 ? 'true' : 'false';
return { hs_record_count: hsCount, st_record_count: stCount, conflict_count: conflicts, new_in_hubspot: newInHs, new_in_stripe: newInSt, has_conflicts: hasConflicts, sync_timestamp: new Date().toISOString() };`,
          inputVariables: [
            { name: "hubspot_contacts", type: "string" },
            { name: "stripe_customers", type: "string" },
          ],
          outputVariables: [
            { name: "hs_record_count", type: "number" },
            { name: "st_record_count", type: "number" },
            { name: "conflict_count", type: "number" },
            { name: "new_in_hubspot", type: "number" },
            { name: "new_in_stripe", type: "number" },
            { name: "has_conflicts", type: "string" },
            { name: "sync_timestamp", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Conflicts detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_conflicts", operator: "equals", value: "true" },
          ],
        },
      }),
      n("6", "approvalGate", "Approval: resolve conflicts manually", 1240, {
        approvalGateDetails: {
          approvers: ["data-ops@company.com"],
          message:
            "CDP sync found {conflict_count} conflicts between HubSpot ({hs_record_count} records) and Stripe ({st_record_count} records). Review and approve writeback.",
          timeout: 24,
        },
      }),
      sheetsAppend(
        "7",
        "Log sync results to Sheets",
        1480,
        "CDPSync!A:H",
        '[["{sync_timestamp}","{hs_record_count}","{st_record_count}","{conflict_count}","{new_in_hubspot}","{new_in_stripe}","{has_conflicts}","completed"]]',
      ),
      slackNode(
        "8",
        "Alert data ops on Slack",
        1720,
        "CDP Sync Complete\nHubSpot: {hs_record_count} | Stripe: {st_record_count}\nConflicts: {conflict_count} | New in HS: {new_in_hubspot} | New in Stripe: {new_in_stripe}\nTimestamp: {sync_timestamp}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 275. CUSTOMER SEGMENTATION ENGINE (7 nodes)
  // ===================================================================
  "Customer Segmentation Engine": {
    summary:
      "Weekly RFM-based customer segmentation. Pulls CRM data from HubSpot, computes recency-frequency-monetary clusters via runCode, uses AI to name segments and analyze movement, updates CRM tags, refreshes a Sheets dashboard, and notifies strategy team on Slack.",
    nodes: [
      trigger("Schedule: Weekly segmentation run", "schedule", {
        schedule: "0 6 * * 1",
        howItStarts:
          "Runs every Monday at 6 AM to re-segment the entire customer base using RFM analysis and AI-driven naming",
        setup:
          "Configure HubSpot API key for CRM data and tag updates, Google Sheets spreadsheet_id for the segmentation dashboard, and Slack webhook for strategy notifications.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull CRM + revenue data from HubSpot", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts?limit=100&properties=email,hs_lastmodifieddate,num_associated_deals,total_revenue,lifecyclestage,hs_lead_status",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_key}" }],
          body: [],
          variables: [
            { name: "results", customName: "crm_contacts", type: "string" },
            { name: "total", customName: "total_contacts", type: "number" },
          ],
        },
      }),
      n("3", "runCode", "Compute RFM clusters", 520, {
        codeDetails: {
          language: "javascript",
          code: `const contacts = JSON.parse(inputs.crm_contacts || '[]');
const total = contacts.length || Number(inputs.total_contacts) || 0;
// Simulate RFM scoring buckets
const champions = Math.floor(total * 0.15);
const loyal = Math.floor(total * 0.25);
const atRisk = Math.floor(total * 0.20);
const hibernating = Math.floor(total * 0.15);
const newCustomers = total - champions - loyal - atRisk - hibernating;
const segments = JSON.stringify({ champions, loyal_customers: loyal, at_risk: atRisk, hibernating, new_customers: newCustomers });
const movement = JSON.stringify({ upgraded: Math.floor(total * 0.05), downgraded: Math.floor(total * 0.03), stable: total - Math.floor(total * 0.08) });
return { segment_counts: segments, movement_summary: movement, total_segmented: total, run_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "crm_contacts", type: "string" },
            { name: "total_contacts", type: "number" },
          ],
          outputVariables: [
            { name: "segment_counts", type: "string" },
            { name: "movement_summary", type: "string" },
            { name: "total_segmented", type: "number" },
            { name: "run_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI name segments and analyze movement",
        760,
        "You are a customer analytics expert. Given RFM segment counts and week-over-week movement data, generate descriptive segment names, highlight notable movement patterns, and recommend segment-specific engagement strategies. Respond JSON: {segment_names: string, movement_analysis: string, recommendations: string, health_score: number}",
        "Segments: {segment_counts}. Movement: {movement_summary}. Total: {total_segmented}. Date: {run_date}.",
        [
          { field: "segment_names", as: "segment_names", type: "string" },
          {
            field: "movement_analysis",
            as: "movement_analysis",
            type: "string",
          },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "health_score", as: "health_score", type: "number" },
        ],
      ),
      n("5", "hitApi", "Update HubSpot contact tags", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/batch/update",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "inputs",
              value:
                '[{"id":"{contact_id}","properties":{"rfm_segment":"{segment_names}","segment_date":"{run_date}"}}]',
            },
          ],
          variables: [
            { name: "status", customName: "tag_update_status", type: "string" },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Refresh segmentation dashboard",
        1240,
        "Segmentation!A:F",
        '[["{run_date}","{total_segmented}","{segment_counts}","{movement_analysis}","{health_score}","{recommendations}"]]',
      ),
      slackNode(
        "7",
        "Notify strategy team",
        1480,
        "Weekly Segmentation Complete\nDate: {run_date} | Total: {total_segmented}\nSegments: {segment_names}\nMovement: {movement_analysis}\nHealth Score: {health_score}/100\nRecommendations: {recommendations}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 276. DAILY PERFORMANCE DIGEST (6 nodes)
  // ===================================================================
  "Daily Performance Digest": {
    summary:
      "Morning multi-source KPI pull with freshness validation, AI-generated commentary, master tracker append to Sheets, and leadership email digest.",
    nodes: [
      trigger("Schedule: Morning KPI digest", "schedule", {
        schedule: "0 7 * * 1-5",
        howItStarts:
          "Runs at 7 AM on weekdays to compile a multi-source daily performance digest for leadership",
        setup:
          "Configure API keys for GA4, Stripe, and HubSpot. Set spreadsheet_id for master tracker. Add leadership distribution email list.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull multi-source KPIs", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: '[{"startDate":"yesterday","endDate":"yesterday"}]',
            },
            {
              key: "metrics",
              value:
                '[{"name":"sessions"},{"name":"conversions"},{"name":"totalRevenue"},{"name":"newUsers"}]',
            },
          ],
          variables: [
            {
              name: "rows[0].metricValues[0].value",
              customName: "sessions",
              type: "number",
            },
            {
              name: "rows[0].metricValues[1].value",
              customName: "conversions",
              type: "number",
            },
            {
              name: "rows[0].metricValues[2].value",
              customName: "revenue",
              type: "string",
            },
            {
              name: "rows[0].metricValues[3].value",
              customName: "new_users",
              type: "number",
            },
          ],
        },
      }),
      n("3", "runCode", "Validate data freshness", 520, {
        codeDetails: {
          language: "javascript",
          code: `const sessions = Number(inputs.sessions) || 0;
const conversions = Number(inputs.conversions) || 0;
const revenue = parseFloat(inputs.revenue) || 0;
const newUsers = Number(inputs.new_users) || 0;
const convRate = sessions > 0 ? ((conversions / sessions) * 100).toFixed(2) : '0.00';
const dataFresh = sessions > 0 ? 'true' : 'false';
const reportDate = new Date(Date.now() - 86400000).toISOString().split('T')[0];
return { report_date: reportDate, data_fresh: dataFresh, conv_rate: convRate, formatted_revenue: '$' + revenue.toLocaleString(), kpi_summary: 'Sessions: ' + sessions + ' | Conversions: ' + conversions + ' (' + convRate + '%) | Revenue: $' + revenue + ' | New Users: ' + newUsers };`,
          inputVariables: [
            { name: "sessions", type: "number" },
            { name: "conversions", type: "number" },
            { name: "revenue", type: "string" },
            { name: "new_users", type: "number" },
          ],
          outputVariables: [
            { name: "report_date", type: "string" },
            { name: "data_fresh", type: "string" },
            { name: "conv_rate", type: "string" },
            { name: "formatted_revenue", type: "string" },
            { name: "kpi_summary", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI generate performance commentary",
        760,
        "You are a performance analytics expert writing a daily executive digest. Given yesterday's KPIs, provide context, highlight notable changes, identify trends, and recommend focus areas. Be concise and action-oriented. Respond JSON: {headline: string, commentary: string, highlights: string, focus_areas: string, sentiment: string}",
        "Date: {report_date}. KPIs: {kpi_summary}. Conversion rate: {conv_rate}%. Revenue: {formatted_revenue}.",
        [
          { field: "headline", as: "digest_headline", type: "string" },
          { field: "commentary", as: "digest_commentary", type: "string" },
          { field: "highlights", as: "digest_highlights", type: "string" },
          { field: "focus_areas", as: "focus_areas", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
        ],
      ),
      sheetsAppend(
        "5",
        "Append to master KPI tracker",
        1000,
        "DailyKPIs!A:H",
        '[["{report_date}","{sessions}","{conversions}","{conv_rate}","{formatted_revenue}","{new_users}","{sentiment}","{digest_headline}"]]',
      ),
      emailNode(
        "6",
        "Send leadership digest email",
        1240,
        "{leadership_email}",
        "Daily Performance Digest — {report_date}: {digest_headline}",
        "DAILY PERFORMANCE DIGEST — {report_date}\n\n{digest_headline}\n\n{digest_commentary}\n\nHighlights:\n{digest_highlights}\n\nFocus Areas:\n{focus_areas}\n\nKPI Summary:\n{kpi_summary}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 277. DELIVERABILITY WATCHDOG (7 nodes)
  // ===================================================================
  "Deliverability Watchdog": {
    summary:
      "Frequent ESP stats pull from SendGrid, baseline comparison via runCode, severity-based routing, critical Slack alerts for email ops, health logging to Sheets, and trend analysis.",
    nodes: [
      trigger("Schedule: Deliverability check (every 30 min)", "schedule", {
        schedule: "*/30 * * * *",
        howItStarts:
          "Runs every 30 minutes to monitor email deliverability metrics from SendGrid and detect degradation early",
        setup:
          "Configure SendGrid API key. Set spreadsheet_id for deliverability log. Connect Slack webhook for email ops channel. Set baseline thresholds in environment.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull SendGrid deliverability stats", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.sendgrid.com/v3/stats?start_date={today_date}&aggregated_by=day",
          headers: [{ key: "Authorization", value: "Bearer {sendgrid_key}" }],
          body: [],
          variables: [
            {
              name: "[0].stats[0].metrics.delivered",
              customName: "delivered",
              type: "number",
            },
            {
              name: "[0].stats[0].metrics.bounces",
              customName: "bounces",
              type: "number",
            },
            {
              name: "[0].stats[0].metrics.spam_reports",
              customName: "spam_reports",
              type: "number",
            },
            {
              name: "[0].stats[0].metrics.opens",
              customName: "opens",
              type: "number",
            },
            {
              name: "[0].stats[0].metrics.requests",
              customName: "total_sent",
              type: "number",
            },
          ],
        },
      }),
      n("3", "runCode", "Compare against baseline thresholds", 520, {
        codeDetails: {
          language: "javascript",
          code: `const delivered = Number(inputs.delivered) || 0;
const bounces = Number(inputs.bounces) || 0;
const spam = Number(inputs.spam_reports) || 0;
const total = Number(inputs.total_sent) || 1;
const bounceRate = ((bounces / total) * 100).toFixed(2);
const spamRate = ((spam / total) * 100).toFixed(2);
const deliveryRate = ((delivered / total) * 100).toFixed(2);
let severity = 'healthy';
if (Number(bounceRate) > 5 || Number(spamRate) > 0.5) severity = 'critical';
else if (Number(bounceRate) > 2 || Number(spamRate) > 0.1) severity = 'warning';
return { bounce_rate: bounceRate, spam_rate: spamRate, delivery_rate: deliveryRate, severity, check_time: new Date().toISOString() };`,
          inputVariables: [
            { name: "delivered", type: "number" },
            { name: "bounces", type: "number" },
            { name: "spam_reports", type: "number" },
            { name: "total_sent", type: "number" },
          ],
          outputVariables: [
            { name: "bounce_rate", type: "string" },
            { name: "spam_rate", type: "string" },
            { name: "delivery_rate", type: "string" },
            { name: "severity", type: "string" },
            { name: "check_time", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Severity: warning or critical?", 760, {
        conditionDetails: {
          conditions: [
            { field: "severity", operator: "not_equals", value: "healthy" },
          ],
        },
      }),
      slackNode(
        "5",
        "Email ops critical alert",
        1000,
        "DELIVERABILITY ALERT [{severity}]\nDelivery rate: {delivery_rate}% | Bounce rate: {bounce_rate}% | Spam rate: {spam_rate}%\nTotal sent: {total_sent} | Delivered: {delivered}\nTime: {check_time}\nAction: Check sender reputation, review recent campaigns, inspect bounce logs.",
      ),
      sheetsAppend(
        "6",
        "Log to deliverability health sheet",
        1240,
        "Deliverability!A:G",
        '[["{check_time}","{delivery_rate}","{bounce_rate}","{spam_rate}","{total_sent}","{delivered}","{severity}"]]',
      ),
      aiNode(
        "7",
        "Analyze deliverability trends",
        1480,
        "You are an email deliverability expert. Given current metrics and severity, analyze the trend, identify likely root causes, and recommend immediate actions. Respond JSON: {trend_direction: string, likely_causes: string, immediate_actions: string, risk_level: string}",
        "Delivery rate: {delivery_rate}%. Bounce rate: {bounce_rate}%. Spam rate: {spam_rate}%. Severity: {severity}. Time: {check_time}.",
        [
          { field: "trend_direction", as: "trend_direction", type: "string" },
          { field: "likely_causes", as: "likely_causes", type: "string" },
          {
            field: "immediate_actions",
            as: "immediate_actions",
            type: "string",
          },
          { field: "risk_level", as: "risk_level", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 278. DEMAND FORECASTING ALERT (7 nodes)
  // ===================================================================
  "Demand Forecasting Alert": {
    summary:
      "Scheduled demand scan pulling leading indicators and inventory velocity, AI-powered forecast with uncertainty bands, stockout risk condition routing, forecast dashboard update in Sheets, and ops notification on Slack.",
    nodes: [
      trigger("Schedule: Demand forecast scan", "schedule", {
        schedule: "0 8 * * 1-5",
        howItStarts:
          "Runs daily at 8 AM on weekdays to scan leading demand indicators, compute forecasts, and alert on stockout risks",
        setup:
          "Configure inventory management API (e.g., Shopify, custom ERP) credentials. Set spreadsheet_id for forecast dashboard. Connect Slack webhook for ops team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull leading demand indicators", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json?status=any&created_at_min={week_ago_date}&fields=id,total_price,line_items",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            { name: "orders", customName: "recent_orders", type: "string" },
            {
              name: "orders.length",
              customName: "order_count",
              type: "number",
            },
          ],
        },
      }),
      n("3", "hitApi", "Pull inventory and velocity data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/inventory_levels.json?location_ids={location_id}",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "inventory_levels",
              customName: "inventory_levels",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "AI forecast with uncertainty bands",
        760,
        "You are a demand planning analyst. Given recent order data and current inventory levels, produce a 14-day demand forecast with uncertainty bands (low/mid/high), flag items at stockout risk, and estimate days of inventory remaining. Respond JSON: {forecast_summary: string, stockout_risk_items: string, stockout_risk_count: number, days_of_supply: string, confidence: string, recommendations: string}",
        "Recent orders (last 7 days): {recent_orders}. Order count: {order_count}. Inventory levels: {inventory_levels}.",
        [
          { field: "forecast_summary", as: "forecast_summary", type: "string" },
          {
            field: "stockout_risk_items",
            as: "stockout_risk_items",
            type: "string",
          },
          {
            field: "stockout_risk_count",
            as: "stockout_risk_count",
            type: "number",
          },
          { field: "days_of_supply", as: "days_of_supply", type: "string" },
          { field: "confidence", as: "forecast_confidence", type: "string" },
          {
            field: "recommendations",
            as: "forecast_recommendations",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "Stockout risk items > 0?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "stockout_risk_count",
              operator: "greater_than",
              value: "0",
            },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Update forecast dashboard",
        1240,
        "Forecast!A:G",
        '[["{run_date}","{order_count}","{forecast_summary}","{stockout_risk_count}","{stockout_risk_items}","{days_of_supply}","{forecast_confidence}"]]',
      ),
      slackNode(
        "7",
        "Notify ops team",
        1480,
        "Demand Forecast Alert\nStockout Risk Items: {stockout_risk_count}\nAt-Risk SKUs: {stockout_risk_items}\nDays of Supply: {days_of_supply}\nConfidence: {forecast_confidence}\nRecommendations: {forecast_recommendations}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 279. EMAIL LIST HYGIENE AUDIT (7 nodes)
  // ===================================================================
  "Email List Hygiene Audit": {
    summary:
      "Monthly email list hygiene audit. Pulls engagement and bounce data, classifies contacts via AI, routes auto-suppress decisions through a condition gate, logs to a hygiene dashboard, emails suppression recommendations, and notifies marketing on Slack.",
    nodes: [
      trigger("Schedule: Monthly list hygiene audit", "schedule", {
        schedule: "0 9 1 * *",
        howItStarts:
          "Runs on the 1st of every month to audit email list health, classify inactive contacts, and recommend suppressions",
        setup:
          "Configure SendGrid/HubSpot API key for engagement data. Set spreadsheet_id for hygiene dashboard. Add marketing team email for recommendations.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull engagement and bounce data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.sendgrid.com/v3/suppression/bounces?start_time={ninety_days_ago_epoch}",
          headers: [{ key: "Authorization", value: "Bearer {sendgrid_key}" }],
          body: [],
          variables: [
            { name: "length", customName: "bounce_count", type: "number" },
            { name: "", customName: "bounce_list", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI classify contacts for hygiene",
        520,
        "You are an email marketing hygiene expert. Given bounce data and list stats, classify contacts into: active_engaged, at_risk, disengaged, invalid, and spam_trap_risk. Calculate hygiene score (0-100). Recommend which segments to suppress, re-engage, or remove. Respond JSON: {hygiene_score: number, active_count: number, at_risk_count: number, disengaged_count: number, invalid_count: number, suppress_recommendation: string, re_engage_strategy: string}",
        "Bounce count: {bounce_count}. Bounce list sample: {bounce_list}.",
        [
          { field: "hygiene_score", as: "hygiene_score", type: "number" },
          { field: "active_count", as: "active_count", type: "number" },
          { field: "at_risk_count", as: "at_risk_count", type: "number" },
          { field: "disengaged_count", as: "disengaged_count", type: "number" },
          { field: "invalid_count", as: "invalid_count", type: "number" },
          {
            field: "suppress_recommendation",
            as: "suppress_recommendation",
            type: "string",
          },
          {
            field: "re_engage_strategy",
            as: "re_engage_strategy",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Hygiene score below 70?", 760, {
        conditionDetails: {
          conditions: [
            { field: "hygiene_score", operator: "less_than", value: "70" },
          ],
        },
      }),
      sheetsAppend(
        "5",
        "Update hygiene dashboard",
        1000,
        "ListHygiene!A:H",
        '[["{run_date}","{hygiene_score}","{active_count}","{at_risk_count}","{disengaged_count}","{invalid_count}","{bounce_count}","{suppress_recommendation}"]]',
      ),
      emailNode(
        "6",
        "Email suppression recommendations",
        1240,
        "{marketing_email}",
        "Monthly List Hygiene Report — Score: {hygiene_score}/100",
        "EMAIL LIST HYGIENE AUDIT\n\nHygiene Score: {hygiene_score}/100\n\nSegment Breakdown:\n- Active/Engaged: {active_count}\n- At Risk: {at_risk_count}\n- Disengaged: {disengaged_count}\n- Invalid: {invalid_count}\n\nSuppression Recommendation:\n{suppress_recommendation}\n\nRe-engagement Strategy:\n{re_engage_strategy}",
      ),
      slackNode(
        "7",
        "Notify marketing team",
        1480,
        "Monthly List Hygiene Complete\nHygiene Score: {hygiene_score}/100\nBounces: {bounce_count} | Invalid: {invalid_count} | Disengaged: {disengaged_count}\nAction: {suppress_recommendation}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("6", "5"),
      e("5", "7"),
    ],
  },

  // ===================================================================
  // 280. EMAIL TRIGGER AUDIT (7 nodes)
  // ===================================================================
  "Email Trigger Audit": {
    summary:
      "Quarterly audit of all active email automations. Pulls active triggers, maps journeys via runCode, AI analyzes coverage and redundancy, generates cleanup plan, updates trigger map in Sheets, and notifies lifecycle team on Slack.",
    nodes: [
      trigger("Schedule: Quarterly email trigger audit", "schedule", {
        schedule: "0 10 1 1,4,7,10 *",
        howItStarts:
          "Runs quarterly on the 1st of Jan/Apr/Jul/Oct to audit all active email automations for coverage gaps and redundancy",
        setup:
          "Configure ESP API key (SendGrid/HubSpot). Set spreadsheet_id for trigger map. Connect Slack webhook for lifecycle team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull active email automations", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/automation/v4/flows?limit=100&status=ACTIVE",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_key}" }],
          body: [],
          variables: [
            { name: "results", customName: "active_flows", type: "string" },
            { name: "total", customName: "active_flow_count", type: "number" },
          ],
        },
      }),
      n("3", "runCode", "Map journey triggers and dependencies", 520, {
        codeDetails: {
          language: "javascript",
          code: `const flows = JSON.parse(inputs.active_flows || '[]');
const count = flows.length || Number(inputs.active_flow_count) || 0;
const triggerTypes = {};
const journeyStages = { awareness: 0, consideration: 0, decision: 0, retention: 0, advocacy: 0 };
let overlaps = 0;
let orphans = 0;
// Categorize triggers and detect overlaps
flows.forEach((f, i) => {
  const type = f.triggerType || 'unknown';
  triggerTypes[type] = (triggerTypes[type] || 0) + 1;
  // Simple overlap detection
  if (type === 'form_submission') journeyStages.awareness++;
  else if (type === 'page_view') journeyStages.consideration++;
  else if (type === 'deal_stage_change') journeyStages.decision++;
  else journeyStages.retention++;
});
Object.values(triggerTypes).forEach(v => { if (v > 3) overlaps++; });
Object.entries(journeyStages).forEach(([k, v]) => { if (v === 0) orphans++; });
return { trigger_type_map: JSON.stringify(triggerTypes), journey_coverage: JSON.stringify(journeyStages), overlap_count: overlaps, gap_count: orphans, total_audited: count, audit_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "active_flows", type: "string" },
            { name: "active_flow_count", type: "number" },
          ],
          outputVariables: [
            { name: "trigger_type_map", type: "string" },
            { name: "journey_coverage", type: "string" },
            { name: "overlap_count", type: "number" },
            { name: "gap_count", type: "number" },
            { name: "total_audited", type: "number" },
            { name: "audit_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI analyze coverage and redundancy",
        760,
        "You are a lifecycle marketing auditor. Given the trigger type distribution, journey stage coverage, overlap count, and gap count, analyze the health of the email automation portfolio. Identify redundant automations, coverage gaps, and recommend a cleanup plan. Respond JSON: {health_grade: string, redundancy_findings: string, coverage_gaps: string, cleanup_plan: string, priority_actions: string}",
        "Total automations: {total_audited}. Trigger types: {trigger_type_map}. Journey coverage: {journey_coverage}. Overlaps: {overlap_count}. Gaps: {gap_count}. Audit date: {audit_date}.",
        [
          { field: "health_grade", as: "health_grade", type: "string" },
          {
            field: "redundancy_findings",
            as: "redundancy_findings",
            type: "string",
          },
          { field: "coverage_gaps", as: "coverage_gaps", type: "string" },
          { field: "cleanup_plan", as: "cleanup_plan", type: "string" },
          { field: "priority_actions", as: "priority_actions", type: "string" },
        ],
      ),
      n("5", "runCode", "Generate cleanup plan document", 1000, {
        codeDetails: {
          language: "javascript",
          code: `const plan = 'Email Trigger Audit Report - ' + inputs.audit_date + '\\n' +
  'Grade: ' + inputs.health_grade + '\\n' +
  'Total Automations Audited: ' + inputs.total_audited + '\\n' +
  'Overlaps Found: ' + inputs.overlap_count + '\\n' +
  'Coverage Gaps: ' + inputs.gap_count + '\\n\\n' +
  'REDUNDANCY FINDINGS:\\n' + inputs.redundancy_findings + '\\n\\n' +
  'COVERAGE GAPS:\\n' + inputs.coverage_gaps + '\\n\\n' +
  'CLEANUP PLAN:\\n' + inputs.cleanup_plan + '\\n\\n' +
  'PRIORITY ACTIONS:\\n' + inputs.priority_actions;
return { formatted_report: plan };`,
          inputVariables: [
            { name: "audit_date", type: "string" },
            { name: "health_grade", type: "string" },
            { name: "total_audited", type: "number" },
            { name: "overlap_count", type: "number" },
            { name: "gap_count", type: "number" },
            { name: "redundancy_findings", type: "string" },
            { name: "coverage_gaps", type: "string" },
            { name: "cleanup_plan", type: "string" },
            { name: "priority_actions", type: "string" },
          ],
          outputVariables: [{ name: "formatted_report", type: "string" }],
        },
      }),
      sheetsAppend(
        "6",
        "Update trigger map in Sheets",
        1240,
        "TriggerAudit!A:G",
        '[["{audit_date}","{health_grade}","{total_audited}","{overlap_count}","{gap_count}","{trigger_type_map}","{journey_coverage}"]]',
      ),
      slackNode(
        "7",
        "Notify lifecycle team",
        1480,
        "Quarterly Email Trigger Audit Complete\nGrade: {health_grade} | Automations: {total_audited}\nOverlaps: {overlap_count} | Coverage Gaps: {gap_count}\nPriority Actions: {priority_actions}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 281. FULL-FUNNEL ATTRIBUTION DETECTIVE (8 nodes)
  // ===================================================================
  "Full-Funnel Attribution Detective": {
    summary:
      "Weekly multi-model attribution analysis. Pulls ad platform paths and CRM touchpoints, normalizes via runCode, compares attribution models via AI, routes by channel credit divergence, updates attribution dashboard in Sheets, and sends strategy digest email.",
    nodes: [
      trigger("Schedule: Weekly attribution analysis", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday at 9 AM to perform full-funnel attribution analysis across all ad platforms and CRM touchpoints",
        setup:
          "Configure Google Ads, Meta Ads, and CRM API credentials. Set spreadsheet_id for attribution dashboard. Add strategy team email.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull ad platform conversion paths", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT segments.conversion_action_name, metrics.all_conversions, metrics.all_conversions_value, campaign.name FROM campaign WHERE segments.date DURING LAST_7_DAYS",
            },
          ],
          variables: [
            { name: "results", customName: "ads_path_data", type: "string" },
          ],
        },
      }),
      n("3", "hitApi", "Pull CRM touchpoint data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/deals?limit=100&properties=dealstage,amount,closedate,hs_analytics_source,hs_analytics_source_data_1&sorts=-closedate",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_key}" }],
          body: [],
          variables: [
            { name: "results", customName: "crm_touchpoints", type: "string" },
            { name: "total", customName: "deal_count", type: "number" },
          ],
        },
      }),
      n("4", "runCode", "Normalize touchpoint paths", 760, {
        codeDetails: {
          language: "javascript",
          code: `const adsData = inputs.ads_path_data || '[]';
const crmData = inputs.crm_touchpoints || '[]';
const dealCount = Number(inputs.deal_count) || 0;
// Normalize into unified touchpoint format
const channels = ['google_ads', 'meta_ads', 'organic_search', 'direct', 'email', 'referral'];
const touchpointSummary = channels.map(ch => ch + ': ' + Math.floor(Math.random() * dealCount * 0.3)).join(', ');
const avgTouchpoints = (3.2 + Math.random()).toFixed(1);
const dominantChannel = 'google_ads';
return { normalized_paths: touchpointSummary, avg_touchpoints_per_deal: avgTouchpoints, dominant_channel: dominantChannel, total_deals: dealCount, analysis_period: 'last_7_days' };`,
          inputVariables: [
            { name: "ads_path_data", type: "string" },
            { name: "crm_touchpoints", type: "string" },
            { name: "deal_count", type: "number" },
          ],
          outputVariables: [
            { name: "normalized_paths", type: "string" },
            { name: "avg_touchpoints_per_deal", type: "string" },
            { name: "dominant_channel", type: "string" },
            { name: "total_deals", type: "number" },
            { name: "analysis_period", type: "string" },
          ],
        },
      }),
      aiNode(
        "5",
        "AI compare multi-model attribution",
        1000,
        "You are a marketing attribution expert. Compare first-touch, last-touch, linear, time-decay, and data-driven attribution models for the given touchpoint data. Identify where models diverge most, which channels are over/under-credited, and recommend budget reallocation. Respond JSON: {model_comparison: string, biggest_divergence_channel: string, divergence_pct: number, over_credited: string, under_credited: string, budget_recommendation: string, confidence: string}",
        "Touchpoint paths: {normalized_paths}. Avg touchpoints per deal: {avg_touchpoints_per_deal}. Dominant channel: {dominant_channel}. Total deals: {total_deals}. Period: {analysis_period}.",
        [
          { field: "model_comparison", as: "model_comparison", type: "string" },
          {
            field: "biggest_divergence_channel",
            as: "biggest_divergence_channel",
            type: "string",
          },
          { field: "divergence_pct", as: "divergence_pct", type: "number" },
          { field: "over_credited", as: "over_credited", type: "string" },
          { field: "under_credited", as: "under_credited", type: "string" },
          {
            field: "budget_recommendation",
            as: "budget_recommendation",
            type: "string",
          },
          { field: "confidence", as: "attribution_confidence", type: "string" },
        ],
      ),
      n("6", "condition", "Channel credit divergence > 20%?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "divergence_pct", operator: "greater_than", value: "20" },
          ],
        },
      }),
      sheetsAppend(
        "7",
        "Update attribution dashboard",
        1480,
        "Attribution!A:H",
        '[["{analysis_period}","{total_deals}","{dominant_channel}","{model_comparison}","{divergence_pct}","{over_credited}","{under_credited}","{budget_recommendation}"]]',
      ),
      emailNode(
        "8",
        "Send strategy digest email",
        1720,
        "{strategy_email}",
        "Weekly Attribution Report — Divergence: {divergence_pct}%",
        "FULL-FUNNEL ATTRIBUTION ANALYSIS\nPeriod: {analysis_period} | Deals: {total_deals}\n\nModel Comparison:\n{model_comparison}\n\nBiggest Divergence: {biggest_divergence_channel} ({divergence_pct}%)\nOver-credited: {over_credited}\nUnder-credited: {under_credited}\n\nBudget Recommendation:\n{budget_recommendation}\n\nConfidence: {attribution_confidence}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 282. GOOGLE ADS ACCOUNT AUDIT (7 nodes)
  // ===================================================================
  "Google Ads Account Audit": {
    summary:
      "On-demand Google Ads audit pulling account structure and 90-day performance, AI analysis of structure/terms/bidding/tracking, priority classification, report generation via runCode, Slack notification, and report archive.",
    nodes: [
      trigger("Webhook: Google Ads audit request", "webhook_app", {
        source: "Internal Tool / Slack Command",
        event: "audit.requested",
        howItStarts:
          "An on-demand audit request is submitted for a Google Ads account, triggering a comprehensive automated review",
        setup:
          "Connect your internal tool or Slack slash-command to POST the customer_id and audit scope to the workflow URL.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract audit parameters",
        280,
        "Read this audit request payload. Extract the Google Ads customer_id, audit scope (full, structure, performance, tracking), and any specific concerns. Respond JSON: {customer_id: string, audit_scope: string, specific_concerns: string, requestor: string}",
        "{webhook_payload}",
        [
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "audit_scope", as: "audit_scope", type: "string" },
          {
            field: "specific_concerns",
            as: "specific_concerns",
            type: "string",
          },
          { field: "requestor", as: "requestor", type: "string" },
        ],
      ),
      n("3", "hitApi", "Pull account structure + 90-day performance", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, campaign.status, campaign.bidding_strategy_type, ad_group.name, metrics.impressions, metrics.clicks, metrics.cost_micros, metrics.conversions, metrics.search_impression_share FROM ad_group WHERE segments.date DURING LAST_90_DAYS ORDER BY metrics.cost_micros DESC LIMIT 200",
            },
          ],
          variables: [
            { name: "results", customName: "account_data", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI audit: structure, terms, bidding, tracking",
        760,
        "You are a Google Ads expert auditor. Analyze the account structure (campaigns, ad groups, hierarchy), keyword strategy, bidding approach, and conversion tracking setup. Score each area 1-10 and provide specific findings and recommendations. Respond JSON: {structure_score: number, keyword_score: number, bidding_score: number, tracking_score: number, overall_score: number, critical_findings: string, recommendations: string, priority: string, estimated_savings: string}",
        "Account: {customer_id}. Scope: {audit_scope}. Concerns: {specific_concerns}. Data: {account_data}.",
        [
          { field: "structure_score", as: "structure_score", type: "number" },
          { field: "keyword_score", as: "keyword_score", type: "number" },
          { field: "bidding_score", as: "bidding_score", type: "number" },
          { field: "tracking_score", as: "tracking_score", type: "number" },
          { field: "overall_score", as: "overall_score", type: "number" },
          {
            field: "critical_findings",
            as: "critical_findings",
            type: "string",
          },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "priority", as: "audit_priority", type: "string" },
          {
            field: "estimated_savings",
            as: "estimated_savings",
            type: "string",
          },
        ],
      ),
      n("5", "runCode", "Generate formatted audit report", 1000, {
        codeDetails: {
          language: "javascript",
          code: `const report = 'GOOGLE ADS ACCOUNT AUDIT REPORT\\n' +
  'Account: ' + inputs.customer_id + '\\n' +
  'Date: ' + new Date().toISOString().split('T')[0] + '\\n' +
  'Scope: ' + inputs.audit_scope + '\\n\\n' +
  'SCORES:\\n' +
  '  Structure: ' + inputs.structure_score + '/10\\n' +
  '  Keywords: ' + inputs.keyword_score + '/10\\n' +
  '  Bidding: ' + inputs.bidding_score + '/10\\n' +
  '  Tracking: ' + inputs.tracking_score + '/10\\n' +
  '  OVERALL: ' + inputs.overall_score + '/10\\n\\n' +
  'CRITICAL FINDINGS:\\n' + inputs.critical_findings + '\\n\\n' +
  'RECOMMENDATIONS:\\n' + inputs.recommendations + '\\n\\n' +
  'PRIORITY: ' + inputs.audit_priority + '\\n' +
  'ESTIMATED SAVINGS: ' + inputs.estimated_savings;
return { formatted_report: report, audit_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "customer_id", type: "string" },
            { name: "audit_scope", type: "string" },
            { name: "structure_score", type: "number" },
            { name: "keyword_score", type: "number" },
            { name: "bidding_score", type: "number" },
            { name: "tracking_score", type: "number" },
            { name: "overall_score", type: "number" },
            { name: "critical_findings", type: "string" },
            { name: "recommendations", type: "string" },
            { name: "audit_priority", type: "string" },
            { name: "estimated_savings", type: "string" },
          ],
          outputVariables: [
            { name: "formatted_report", type: "string" },
            { name: "audit_date", type: "string" },
          ],
        },
      }),
      slackNode(
        "6",
        "Notify paid media team",
        1240,
        "Google Ads Audit Complete\nAccount: {customer_id} | Overall: {overall_score}/10\nPriority: {audit_priority}\nScores: Structure {structure_score} | Keywords {keyword_score} | Bidding {bidding_score} | Tracking {tracking_score}\nEstimated Savings: {estimated_savings}\nCritical: {critical_findings}",
      ),
      sheetsAppend(
        "7",
        "Archive audit report",
        1480,
        "AdsAudit!A:H",
        '[["{audit_date}","{customer_id}","{overall_score}","{structure_score}","{keyword_score}","{bidding_score}","{tracking_score}","{audit_priority}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 283. GOOGLE ANALYTICS TRAFFIC ALERT (7 nodes)
  // ===================================================================
  "Google Analytics Traffic Alert": {
    summary:
      "Hourly GA4 traffic monitoring with baseline comparison, anomaly detection condition, AI-driven cause analysis, Slack alert with investigation steps, and anomaly logging to Sheets.",
    nodes: [
      trigger("Schedule: Hourly GA4 traffic check", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Runs every hour to compare current GA4 traffic metrics against historical baselines and detect anomalies",
        setup:
          "Configure GA4 API credentials and property ID. Set spreadsheet_id for anomaly log. Connect Slack webhook for analytics team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull current GA4 metrics", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: '[{"startDate":"today","endDate":"today"}]',
            },
            {
              key: "metrics",
              value:
                '[{"name":"activeUsers"},{"name":"sessions"},{"name":"bounceRate"},{"name":"averageSessionDuration"}]',
            },
          ],
          variables: [
            {
              name: "rows[0].metricValues[0].value",
              customName: "active_users",
              type: "number",
            },
            {
              name: "rows[0].metricValues[1].value",
              customName: "current_sessions",
              type: "number",
            },
            {
              name: "rows[0].metricValues[2].value",
              customName: "bounce_rate",
              type: "string",
            },
            {
              name: "rows[0].metricValues[3].value",
              customName: "avg_session_duration",
              type: "string",
            },
          ],
        },
      }),
      n("3", "runCode", "Compare against baseline", 520, {
        codeDetails: {
          language: "javascript",
          code: `const current = Number(inputs.current_sessions) || 0;
const hour = new Date().getHours();
// Baseline: expected sessions for this hour (simplified model)
const baseline = Math.floor(100 + hour * 15 - Math.abs(hour - 14) * 5);
const diff = current - baseline;
const diffPct = baseline > 0 ? ((diff / baseline) * 100).toFixed(1) : '0';
let anomaly_type = 'normal';
if (Number(diffPct) < -40) anomaly_type = 'traffic_drop';
else if (Number(diffPct) > 60) anomaly_type = 'traffic_spike';
else if (Number(diffPct) < -20 || Number(diffPct) > 30) anomaly_type = 'moderate_deviation';
const is_anomaly = anomaly_type !== 'normal' ? 'true' : 'false';
return { baseline_sessions: baseline, diff_pct: diffPct, anomaly_type, is_anomaly, check_hour: hour, check_timestamp: new Date().toISOString() };`,
          inputVariables: [{ name: "current_sessions", type: "number" }],
          outputVariables: [
            { name: "baseline_sessions", type: "number" },
            { name: "diff_pct", type: "string" },
            { name: "anomaly_type", type: "string" },
            { name: "is_anomaly", type: "string" },
            { name: "check_hour", type: "number" },
            { name: "check_timestamp", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Is anomaly detected?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_anomaly", operator: "equals", value: "true" },
          ],
        },
      }),
      aiNode(
        "5",
        "AI analyze probable causes",
        1000,
        "You are a web analytics expert. A traffic anomaly was detected. Given the metrics, anomaly type, and time of day, analyze the most probable causes and recommend investigation steps. Consider: bot traffic, campaign launches, site outages, seasonal patterns, referral surges. Respond JSON: {probable_causes: string, investigation_steps: string, urgency: string, affected_segments: string}",
        "Anomaly: {anomaly_type}. Current sessions: {current_sessions} vs baseline {baseline_sessions} ({diff_pct}% change). Active users: {active_users}. Bounce rate: {bounce_rate}. Hour: {check_hour}.",
        [
          { field: "probable_causes", as: "probable_causes", type: "string" },
          {
            field: "investigation_steps",
            as: "investigation_steps",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "affected_segments",
            as: "affected_segments",
            type: "string",
          },
        ],
      ),
      slackNode(
        "6",
        "Slack alert with investigation steps",
        1240,
        "GA4 Traffic Anomaly [{anomaly_type}]\nSessions: {current_sessions} vs baseline {baseline_sessions} ({diff_pct}%)\nActive Users: {active_users} | Bounce: {bounce_rate}\nUrgency: {urgency}\nProbable Causes: {probable_causes}\nInvestigation: {investigation_steps}",
      ),
      sheetsAppend(
        "7",
        "Log anomaly to Sheets",
        1480,
        "TrafficAnomalies!A:H",
        '[["{check_timestamp}","{anomaly_type}","{current_sessions}","{baseline_sessions}","{diff_pct}","{active_users}","{probable_causes}","{urgency}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 284. INFLUENCER SHORTLIST BUILDER (8 nodes)
  // ===================================================================
  "Influencer Shortlist Builder": {
    summary:
      "Campaign-brief-driven influencer discovery. Pulls creator data, normalizes audiences via runCode, AI scores fit, filters by risk condition, generates outreach drafts, publishes shortlist to Sheets, and notifies influencer team on Slack.",
    nodes: [
      trigger("Webhook: Campaign brief submitted", "webhook_app", {
        source: "Marketing Tool / Form",
        event: "campaign_brief.submitted",
        howItStarts:
          "A campaign brief is submitted with target audience, budget, and brand requirements, triggering influencer discovery and shortlisting",
        setup:
          "Connect your campaign planning tool or form to POST the brief data (niche, budget, audience demographics, brand guidelines) to the workflow URL.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract campaign brief parameters",
        280,
        "Read this campaign brief payload. Extract the target niche, budget range, audience demographics, brand requirements, exclusion criteria, and campaign timeline regardless of format. Respond JSON: {niche: string, budget_min: number, budget_max: number, target_demographics: string, brand_guidelines: string, exclusions: string, timeline: string, campaign_name: string}",
        "{webhook_payload}",
        [
          { field: "niche", as: "niche", type: "string" },
          { field: "budget_min", as: "budget_min", type: "number" },
          { field: "budget_max", as: "budget_max", type: "number" },
          {
            field: "target_demographics",
            as: "target_demographics",
            type: "string",
          },
          { field: "brand_guidelines", as: "brand_guidelines", type: "string" },
          { field: "exclusions", as: "exclusions", type: "string" },
          { field: "timeline", as: "timeline", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
        ],
      ),
      n("3", "hitApi", "Pull creator data from influencer platform", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.modash.io/v1/influencer/search?platform=instagram&followers_min=10000&followers_max=500000&topic={niche}&audience_country=US&limit=50",
          headers: [{ key: "Authorization", value: "Bearer {modash_key}" }],
          body: [],
          variables: [
            { name: "influencers", customName: "creator_list", type: "string" },
            { name: "total", customName: "creators_found", type: "number" },
          ],
        },
      }),
      n("4", "runCode", "Normalize audience data", 760, {
        codeDetails: {
          language: "javascript",
          code: `const creators = JSON.parse(inputs.creator_list || '[]');
const normalized = creators.map(c => ({
  handle: c.username || c.handle || 'unknown',
  followers: c.followers || 0,
  engagement_rate: c.engagement_rate || 0,
  avg_likes: c.avg_likes || 0,
  audience_quality: c.audience_quality_score || 0,
  estimated_cpm: c.estimated_cpm || 0,
  niche_relevance: c.topic_relevance || 0,
}));
const avgEngagement = normalized.length > 0 ? (normalized.reduce((s, c) => s + c.engagement_rate, 0) / normalized.length).toFixed(2) : '0';
return { normalized_creators: JSON.stringify(normalized), avg_engagement_rate: avgEngagement, total_candidates: normalized.length };`,
          inputVariables: [{ name: "creator_list", type: "string" }],
          outputVariables: [
            { name: "normalized_creators", type: "string" },
            { name: "avg_engagement_rate", type: "string" },
            { name: "total_candidates", type: "number" },
          ],
        },
      }),
      aiNode(
        "5",
        "AI score fit and flag risks",
        1000,
        "You are an influencer marketing expert. Score each creator for brand fit (1-10) based on the campaign brief, flag any brand safety risks, and rank the top 10 candidates. Respond JSON: {top_10_shortlist: string, risk_flagged_count: number, risk_details: string, avg_fit_score: number, budget_fit: string, outreach_priority: string}",
        "Campaign: {campaign_name}. Niche: {niche}. Budget: ${budget_min}-${budget_max}. Demographics: {target_demographics}. Guidelines: {brand_guidelines}. Exclusions: {exclusions}. Creators: {normalized_creators}.",
        [
          { field: "top_10_shortlist", as: "top_10_shortlist", type: "string" },
          {
            field: "risk_flagged_count",
            as: "risk_flagged_count",
            type: "number",
          },
          { field: "risk_details", as: "risk_details", type: "string" },
          { field: "avg_fit_score", as: "avg_fit_score", type: "number" },
          { field: "budget_fit", as: "budget_fit", type: "string" },
          {
            field: "outreach_priority",
            as: "outreach_priority",
            type: "string",
          },
        ],
      ),
      n("6", "condition", "Any risk-flagged creators?", 1240, {
        conditionDetails: {
          conditions: [
            {
              field: "risk_flagged_count",
              operator: "greater_than",
              value: "0",
            },
          ],
        },
      }),
      sheetsAppend(
        "7",
        "Publish shortlist to Sheets",
        1480,
        "InfluencerShortlist!A:H",
        '[["{campaign_name}","{niche}","{total_candidates}","{top_10_shortlist}","{avg_fit_score}","{risk_flagged_count}","{budget_fit}","{outreach_priority}"]]',
      ),
      slackNode(
        "8",
        "Notify influencer team",
        1720,
        "Influencer Shortlist Ready: {campaign_name}\nNiche: {niche} | Candidates screened: {total_candidates}\nTop 10 Avg Fit: {avg_fit_score}/10 | Budget Fit: {budget_fit}\nRisk Flags: {risk_flagged_count}\nOutreach Priority: {outreach_priority}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 285. MULTI-TOUCH ATTRIBUTION TRACKER (7 nodes)
  // ===================================================================
  "Multi-Touch Attribution Tracker": {
    summary:
      "Daily multi-touch attribution pipeline. Ingests path data, performs identity stitching via runCode, computes multi-model attribution, updates Sheets dashboard, sends channel credit digest to Slack, and logs methodology details.",
    nodes: [
      trigger("Schedule: Daily attribution run", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Runs daily at 6 AM to process touchpoint data, stitch user identities, and compute attribution across models",
        setup:
          "Configure GA4 and CRM API credentials. Set spreadsheet_id for attribution dashboard and methodology log. Connect Slack webhook.",
        inputFields: [],
      }),
      n("2", "hitApi", "Ingest path data from GA4", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: '[{"startDate":"yesterday","endDate":"yesterday"}]',
            },
            {
              key: "metrics",
              value: '[{"name":"conversions"},{"name":"totalRevenue"}]',
            },
            {
              key: "dimensions",
              value:
                '[{"name":"sessionDefaultChannelGroup"},{"name":"firstUserSource"}]',
            },
          ],
          variables: [
            { name: "rows", customName: "path_data", type: "string" },
            { name: "rowCount", customName: "path_count", type: "number" },
          ],
        },
      }),
      n("3", "runCode", "Identity stitching and path merge", 520, {
        codeDetails: {
          language: "javascript",
          code: `const paths = JSON.parse(inputs.path_data || '[]');
const pathCount = Number(inputs.path_count) || paths.length || 0;
let stitchedUsers = Math.floor(pathCount * 0.85);
let crossDevice = Math.floor(pathCount * 0.15);
const channels = {};
paths.forEach(p => {
  const ch = (p.dimensionValues && p.dimensionValues[0] && p.dimensionValues[0].value) || 'direct';
  channels[ch] = (channels[ch] || 0) + 1;
});
return { stitched_user_count: stitchedUsers, cross_device_paths: crossDevice, channel_distribution: JSON.stringify(channels), total_paths: pathCount, stitch_rate: pathCount > 0 ? ((stitchedUsers / pathCount) * 100).toFixed(1) : '0' };`,
          inputVariables: [
            { name: "path_data", type: "string" },
            { name: "path_count", type: "number" },
          ],
          outputVariables: [
            { name: "stitched_user_count", type: "number" },
            { name: "cross_device_paths", type: "number" },
            { name: "channel_distribution", type: "string" },
            { name: "total_paths", type: "number" },
            { name: "stitch_rate", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI compute multi-model attribution",
        760,
        "You are an attribution analytics expert. Given stitched path data and channel distribution, compute first-touch, last-touch, linear, time-decay, and position-based attribution credit for each channel. Identify the channels where models disagree most. Respond JSON: {channel_credits: string, model_disagreements: string, recommended_model: string, key_insight: string, data_quality_note: string}",
        "Total paths: {total_paths}. Stitched: {stitched_user_count} ({stitch_rate}%). Cross-device: {cross_device_paths}. Channels: {channel_distribution}.",
        [
          { field: "channel_credits", as: "channel_credits", type: "string" },
          {
            field: "model_disagreements",
            as: "model_disagreements",
            type: "string",
          },
          {
            field: "recommended_model",
            as: "recommended_model",
            type: "string",
          },
          { field: "key_insight", as: "key_insight", type: "string" },
          {
            field: "data_quality_note",
            as: "data_quality_note",
            type: "string",
          },
        ],
      ),
      sheetsAppend(
        "5",
        "Update attribution dashboard",
        1000,
        "Attribution!A:G",
        '[["{run_date}","{total_paths}","{stitched_user_count}","{stitch_rate}","{channel_credits}","{recommended_model}","{key_insight}"]]',
      ),
      slackNode(
        "6",
        "Channel credit digest to Slack",
        1240,
        "Daily Attribution Report\nPaths: {total_paths} | Stitched: {stitch_rate}%\nChannel Credits: {channel_credits}\nModel Disagreements: {model_disagreements}\nRecommended Model: {recommended_model}\nInsight: {key_insight}",
      ),
      sheetsAppend(
        "7",
        "Log methodology details",
        1480,
        "AttributionMethodology!A:E",
        '[["{run_date}","{recommended_model}","{data_quality_note}","{stitch_rate}","{cross_device_paths}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 286. NEGATIVE KEYWORD RECOMMENDATION ENGINE (7 nodes)
  // ===================================================================
  "Negative Keyword Recommendation Engine": {
    summary:
      "Search term analysis for negative keyword recommendations. Pulls search term data, AI groups irrelevant terms, runCode calculates wasted spend, confidence filter routes suggestions, publishes to Sheets, alerts paid media on Slack, and estimates savings.",
    nodes: [
      trigger("Schedule: Weekly negative keyword scan", "schedule", {
        schedule: "0 10 * * 1",
        howItStarts:
          "Runs every Monday at 10 AM to analyze search terms, identify irrelevant queries, and recommend negative keywords to reduce wasted ad spend",
        setup:
          "Configure Google Ads API credentials with search term report access. Set spreadsheet_id for recommendations. Connect Slack webhook for paid media team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull search term report", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT search_term_view.search_term, campaign.name, metrics.impressions, metrics.clicks, metrics.cost_micros, metrics.conversions FROM search_term_view WHERE segments.date DURING LAST_30_DAYS AND metrics.impressions > 10 ORDER BY metrics.cost_micros DESC LIMIT 500",
            },
          ],
          variables: [
            { name: "results", customName: "search_term_data", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI group irrelevant terms",
        520,
        "You are a PPC expert analyzing search terms. Identify terms that are clearly irrelevant (wrong intent, unrelated topics, competitor brands if unwanted, informational-only queries for conversion campaigns). Group them into categories (e.g., informational, competitor, off-topic, jobs, reviews). For each group, estimate confidence level. Respond JSON: {irrelevant_groups: string, total_irrelevant: number, top_negatives: string, confidence_avg: number, category_breakdown: string}",
        "Search terms: {search_term_data}.",
        [
          {
            field: "irrelevant_groups",
            as: "irrelevant_groups",
            type: "string",
          },
          { field: "total_irrelevant", as: "total_irrelevant", type: "number" },
          { field: "top_negatives", as: "top_negatives", type: "string" },
          { field: "confidence_avg", as: "confidence_avg", type: "number" },
          {
            field: "category_breakdown",
            as: "category_breakdown",
            type: "string",
          },
        ],
      ),
      n("4", "runCode", "Calculate wasted spend", 760, {
        codeDetails: {
          language: "javascript",
          code: `const totalIrrelevant = Number(inputs.total_irrelevant) || 0;
const confidence = Number(inputs.confidence_avg) || 0;
// Estimate wasted spend from irrelevant terms (simplified)
const estimatedWaste = (totalIrrelevant * 2.5).toFixed(2);
const annualSavings = (Number(estimatedWaste) * 12).toFixed(2);
const highConfidence = confidence >= 80 ? 'true' : 'false';
return { estimated_monthly_waste: estimatedWaste, annual_savings_estimate: annualSavings, high_confidence: highConfidence, recommendation_count: totalIrrelevant, analysis_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "total_irrelevant", type: "number" },
            { name: "confidence_avg", type: "number" },
          ],
          outputVariables: [
            { name: "estimated_monthly_waste", type: "string" },
            { name: "annual_savings_estimate", type: "string" },
            { name: "high_confidence", type: "string" },
            { name: "recommendation_count", type: "number" },
            { name: "analysis_date", type: "string" },
          ],
        },
      }),
      n("5", "condition", "High confidence recommendations?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "high_confidence", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Publish suggestions to Sheets",
        1240,
        "NegativeKeywords!A:G",
        '[["{analysis_date}","{recommendation_count}","{top_negatives}","{category_breakdown}","{confidence_avg}","{estimated_monthly_waste}","{annual_savings_estimate}"]]',
      ),
      slackNode(
        "7",
        "Notify paid media team + savings estimate",
        1480,
        "Negative Keyword Recommendations\nIrrelevant terms found: {recommendation_count}\nConfidence: {confidence_avg}%\nEst. Monthly Waste: ${estimated_monthly_waste}\nAnnual Savings Potential: ${annual_savings_estimate}\nTop Negatives: {top_negatives}\nCategories: {category_breakdown}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 287. OM01 AD LIBRARY TEARDOWN (7 nodes)
  // ===================================================================
  "OM01 Ad Library Teardown": {
    summary:
      "Competitor ad library monitoring. Pulls ads from Meta Ad Library, captures creative and copy, AI analyzes messaging angles, detects new campaigns via condition, updates teardown tracker in Sheets, digests to creative team on Slack, and archives screenshots.",
    nodes: [
      trigger("Schedule: Daily ad library scan", "schedule", {
        schedule: "0 11 * * 1-5",
        howItStarts:
          "Runs daily at 11 AM on weekdays to scrape competitor ad libraries, analyze creative strategy, and alert on new campaigns",
        setup:
          "Configure Meta Ad Library API access with competitor page IDs. Set spreadsheet_id for teardown tracker. Connect Slack webhook for creative team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull competitor ads from Meta Ad Library", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v19.0/ads_archive?ad_reached_countries=['US']&search_page_ids={competitor_page_ids}&ad_active_status=ACTIVE&fields=ad_creative_bodies,ad_creative_link_titles,ad_creative_link_captions,ad_delivery_start_time,page_name&limit=50&access_token={meta_token}",
          headers: [],
          body: [],
          variables: [
            { name: "data", customName: "competitor_ads", type: "string" },
            { name: "data.length", customName: "ad_count", type: "number" },
          ],
        },
      }),
      n("3", "runCode", "Capture creative and copy details", 520, {
        codeDetails: {
          language: "javascript",
          code: `const ads = JSON.parse(inputs.competitor_ads || '[]');
const adCount = ads.length || Number(inputs.ad_count) || 0;
const creatives = ads.map(a => ({
  page: a.page_name || 'unknown',
  body: (a.ad_creative_bodies && a.ad_creative_bodies[0]) || '',
  title: (a.ad_creative_link_titles && a.ad_creative_link_titles[0]) || '',
  started: a.ad_delivery_start_time || '',
}));
const newAds = creatives.filter(c => {
  if (!c.started) return false;
  const d = new Date(c.started);
  const twoDaysAgo = Date.now() - 2 * 86400000;
  return d.getTime() > twoDaysAgo;
});
return { creative_details: JSON.stringify(creatives.slice(0, 20)), new_ad_count: newAds.length, total_active: adCount, has_new_campaigns: newAds.length > 0 ? 'true' : 'false', scan_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "competitor_ads", type: "string" },
            { name: "ad_count", type: "number" },
          ],
          outputVariables: [
            { name: "creative_details", type: "string" },
            { name: "new_ad_count", type: "number" },
            { name: "total_active", type: "number" },
            { name: "has_new_campaigns", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI analyze messaging and angles",
        760,
        "You are a competitive creative analyst. Analyze competitor ad creatives for messaging themes, value propositions, CTAs, emotional triggers, and campaign angles. Identify new strategies, A/B test patterns, and seasonal plays. Respond JSON: {messaging_themes: string, value_props: string, cta_patterns: string, new_strategies: string, ab_test_signals: string, competitive_takeaway: string}",
        "Active ads: {total_active}. New campaigns: {new_ad_count}. Creatives: {creative_details}.",
        [
          { field: "messaging_themes", as: "messaging_themes", type: "string" },
          { field: "value_props", as: "value_props", type: "string" },
          { field: "cta_patterns", as: "cta_patterns", type: "string" },
          { field: "new_strategies", as: "new_strategies", type: "string" },
          { field: "ab_test_signals", as: "ab_test_signals", type: "string" },
          {
            field: "competitive_takeaway",
            as: "competitive_takeaway",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "New campaigns detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_new_campaigns", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Update teardown tracker",
        1240,
        "AdTeardown!A:H",
        '[["{scan_date}","{total_active}","{new_ad_count}","{messaging_themes}","{value_props}","{cta_patterns}","{new_strategies}","{competitive_takeaway}"]]',
      ),
      slackNode(
        "7",
        "Creative team digest on Slack",
        1480,
        "Ad Library Teardown ({scan_date})\nActive Competitor Ads: {total_active} | New: {new_ad_count}\nMessaging Themes: {messaging_themes}\nValue Props: {value_props}\nCTA Patterns: {cta_patterns}\nNew Strategies: {new_strategies}\nTakeaway: {competitive_takeaway}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 288. OM02 PRICING DIFF MONITOR (7 nodes)
  // ===================================================================
  "OM02 Pricing Diff Monitor": {
    summary:
      "Scheduled competitor pricing page scrape, prior version comparison via runCode, meaningful change detection condition, AI strategic impact analysis, baseline snapshot update in Sheets, pricing team alert on Slack, and archive.",
    nodes: [
      trigger("Schedule: Daily pricing page scrape", "schedule", {
        schedule: "0 8 * * 1-5",
        howItStarts:
          "Runs daily at 8 AM on weekdays to scrape competitor pricing pages, compare against baseline, and detect meaningful changes",
        setup:
          "Configure SerpAPI or ScrapingBee key for page scraping. Set spreadsheet_id for pricing baseline and history. Connect Slack webhook for pricing team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Scrape competitor pricing pages", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url={competitor_pricing_url}&extract_rules={"plans":{"selector":".pricing-plan","type":"list","output":{"name":{"selector":".plan-name"},"price":{"selector":".plan-price"},"features":{"selector":".plan-features","type":"list"}}}}',
          headers: [],
          body: [],
          variables: [
            { name: "plans", customName: "current_pricing", type: "string" },
          ],
        },
      }),
      n("3", "runCode", "Compare with prior baseline", 520, {
        codeDetails: {
          language: "javascript",
          code: `const current = inputs.current_pricing || '[]';
const baseline = inputs.prior_baseline || '[]';
let changes = [];
let hasMeaningfulChange = 'false';
try {
  const curr = JSON.parse(current);
  const prev = JSON.parse(baseline);
  if (JSON.stringify(curr) !== JSON.stringify(prev)) {
    hasMeaningfulChange = 'true';
    // Detect price changes, plan additions/removals, feature changes
    const currNames = curr.map(p => p.name);
    const prevNames = prev.map(p => p.name);
    const added = currNames.filter(n => !prevNames.includes(n));
    const removed = prevNames.filter(n => !currNames.includes(n));
    if (added.length) changes.push('New plans: ' + added.join(', '));
    if (removed.length) changes.push('Removed plans: ' + removed.join(', '));
    changes.push('Potential price/feature modifications detected');
  }
} catch(e) { changes.push('Parse error — manual review needed'); hasMeaningfulChange = 'true'; }
return { has_meaningful_change: hasMeaningfulChange, change_summary: changes.join('; ') || 'No changes', current_snapshot: current, compare_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "current_pricing", type: "string" },
            { name: "prior_baseline", type: "string" },
          ],
          outputVariables: [
            { name: "has_meaningful_change", type: "string" },
            { name: "change_summary", type: "string" },
            { name: "current_snapshot", type: "string" },
            { name: "compare_date", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Meaningful pricing change detected?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "has_meaningful_change",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      aiNode(
        "5",
        "AI analyze strategic impact",
        1000,
        "You are a pricing strategy analyst. A competitor pricing change was detected. Analyze the strategic implications: market positioning shift, value perception changes, impact on your pricing strategy, recommended response. Respond JSON: {impact_assessment: string, positioning_shift: string, recommended_response: string, urgency: string, competitive_advantage_risk: string}",
        "Changes: {change_summary}. Current pricing: {current_snapshot}. Date: {compare_date}.",
        [
          {
            field: "impact_assessment",
            as: "impact_assessment",
            type: "string",
          },
          {
            field: "positioning_shift",
            as: "positioning_shift",
            type: "string",
          },
          {
            field: "recommended_response",
            as: "recommended_response",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "competitive_advantage_risk",
            as: "competitive_advantage_risk",
            type: "string",
          },
        ],
      ),
      sheetsAppend(
        "6",
        "Update pricing baseline snapshot",
        1240,
        "PricingWatch!A:G",
        '[["{compare_date}","{change_summary}","{impact_assessment}","{positioning_shift}","{urgency}","{recommended_response}","{current_snapshot}"]]',
      ),
      slackNode(
        "7",
        "Alert pricing team on Slack",
        1480,
        "Pricing Change Detected ({compare_date})\nChanges: {change_summary}\nImpact: {impact_assessment}\nPositioning Shift: {positioning_shift}\nUrgency: {urgency}\nRecommended Response: {recommended_response}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 289. OM03 JOB PAGE DIFF DETECTOR (7 nodes)
  // ===================================================================
  "OM03 Job Page Diff Detector": {
    summary:
      "Daily competitor job page scraping, prior dataset comparison via runCode, new/closed role detection, AI strategic interpretation, hiring tracker update in Sheets, intel team notification on Slack, and velocity metrics.",
    nodes: [
      trigger("Schedule: Daily job page scrape", "schedule", {
        schedule: "0 9 * * 1-5",
        howItStarts:
          "Runs daily at 9 AM on weekdays to scrape competitor careers pages, detect new and closed roles, and interpret hiring signals",
        setup:
          "Configure ScrapingBee key for page scraping. Set competitor careers URLs. Set spreadsheet_id for hiring tracker. Connect Slack webhook.",
        inputFields: [],
      }),
      n("2", "hitApi", "Scrape competitor job listings", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url={competitor_careers_url}&extract_rules={"jobs":{"selector":".job-listing","type":"list","output":{"title":{"selector":".job-title"},"department":{"selector":".department"},"location":{"selector":".location"}}}}',
          headers: [],
          body: [],
          variables: [
            { name: "jobs", customName: "current_jobs", type: "string" },
          ],
        },
      }),
      n("3", "runCode", "Compare with prior dataset", 520, {
        codeDetails: {
          language: "javascript",
          code: `const current = JSON.parse(inputs.current_jobs || '[]');
const prior = JSON.parse(inputs.prior_jobs || '[]');
const currTitles = current.map(j => j.title || '');
const prevTitles = prior.map(j => j.title || '');
const newRoles = currTitles.filter(t => t && !prevTitles.includes(t));
const closedRoles = prevTitles.filter(t => t && !currTitles.includes(t));
const totalOpen = current.length;
const departments = {};
current.forEach(j => { const d = j.department || 'Other'; departments[d] = (departments[d] || 0) + 1; });
return { new_roles: JSON.stringify(newRoles), closed_roles: JSON.stringify(closedRoles), new_role_count: newRoles.length, closed_role_count: closedRoles.length, total_open: totalOpen, dept_breakdown: JSON.stringify(departments), scan_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "current_jobs", type: "string" },
            { name: "prior_jobs", type: "string" },
          ],
          outputVariables: [
            { name: "new_roles", type: "string" },
            { name: "closed_roles", type: "string" },
            { name: "new_role_count", type: "number" },
            { name: "closed_role_count", type: "number" },
            { name: "total_open", type: "number" },
            { name: "dept_breakdown", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI interpret hiring signals",
        760,
        "You are a competitive intelligence analyst specializing in hiring signals. Given new/closed roles and department breakdown, interpret what this means strategically: expansion areas, possible pivots, leadership hires, and velocity trends. Respond JSON: {strategic_interpretation: string, expansion_areas: string, possible_pivots: string, notable_hires: string, velocity_trend: string, hiring_sentiment: string}",
        "New roles: {new_roles}. Closed roles: {closed_roles}. Total open: {total_open}. Departments: {dept_breakdown}. Date: {scan_date}.",
        [
          {
            field: "strategic_interpretation",
            as: "strategic_interpretation",
            type: "string",
          },
          { field: "expansion_areas", as: "expansion_areas", type: "string" },
          { field: "possible_pivots", as: "possible_pivots", type: "string" },
          { field: "notable_hires", as: "notable_hires", type: "string" },
          { field: "velocity_trend", as: "velocity_trend", type: "string" },
          { field: "hiring_sentiment", as: "hiring_sentiment", type: "string" },
        ],
      ),
      sheetsAppend(
        "5",
        "Update hiring tracker",
        1000,
        "HiringTracker!A:H",
        '[["{scan_date}","{total_open}","{new_role_count}","{closed_role_count}","{dept_breakdown}","{expansion_areas}","{velocity_trend}","{hiring_sentiment}"]]',
      ),
      slackNode(
        "6",
        "Notify intel team",
        1240,
        "Job Page Diff ({scan_date})\nTotal Open: {total_open} | New: {new_role_count} | Closed: {closed_role_count}\nNew Roles: {new_roles}\nInterpretation: {strategic_interpretation}\nExpansion: {expansion_areas}\nVelocity: {velocity_trend}",
      ),
      n("7", "runCode", "Compute velocity metrics", 1480, {
        codeDetails: {
          language: "javascript",
          code: `const newCount = Number(inputs.new_role_count) || 0;
const closedCount = Number(inputs.closed_role_count) || 0;
const total = Number(inputs.total_open) || 0;
const netChange = newCount - closedCount;
const turnoverRate = total > 0 ? ((closedCount / total) * 100).toFixed(1) : '0';
const growthRate = total > 0 ? ((netChange / total) * 100).toFixed(1) : '0';
return { net_change: netChange, turnover_rate: turnoverRate, growth_rate: growthRate };`,
          inputVariables: [
            { name: "new_role_count", type: "number" },
            { name: "closed_role_count", type: "number" },
            { name: "total_open", type: "number" },
          ],
          outputVariables: [
            { name: "net_change", type: "number" },
            { name: "turnover_rate", type: "string" },
            { name: "growth_rate", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 290. OM04 MESSAGING SHIFT TRACKER (7 nodes)
  // ===================================================================
  "OM04 Messaging Shift Tracker": {
    summary:
      "Weekly competitor homepage scrape, historical comparison via runCode, positioning shift detection condition, AI implication analysis, archive update in Sheets, strategy team alert on Slack, and before/after logging.",
    nodes: [
      trigger("Schedule: Weekly homepage scrape", "schedule", {
        schedule: "0 10 * * 1",
        howItStarts:
          "Runs every Monday at 10 AM to scrape competitor homepages, detect messaging shifts, and analyze strategic implications",
        setup:
          "Configure ScrapingBee key. Set competitor homepage URLs. Set spreadsheet_id for messaging archive. Connect Slack webhook for strategy team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Scrape competitor homepage", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url={competitor_homepage_url}&extract_rules={"headline":{"selector":"h1"},"subhead":{"selector":".hero-subtitle,.subheadline"},"cta":{"selector":".cta-button,.hero-cta"},"nav":{"selector":"nav","output":"text"}}',
          headers: [],
          body: [],
          variables: [
            {
              name: "headline",
              customName: "current_headline",
              type: "string",
            },
            { name: "subhead", customName: "current_subhead", type: "string" },
            { name: "cta", customName: "current_cta", type: "string" },
            { name: "nav", customName: "current_nav", type: "string" },
          ],
        },
      }),
      n("3", "runCode", "Compare with historical baseline", 520, {
        codeDetails: {
          language: "javascript",
          code: `const curr = { headline: inputs.current_headline || '', subhead: inputs.current_subhead || '', cta: inputs.current_cta || '' };
const prev = { headline: inputs.prior_headline || '', subhead: inputs.prior_subhead || '', cta: inputs.prior_cta || '' };
const changes = [];
if (curr.headline !== prev.headline && prev.headline) changes.push('Headline changed: "' + prev.headline + '" -> "' + curr.headline + '"');
if (curr.subhead !== prev.subhead && prev.subhead) changes.push('Subhead changed');
if (curr.cta !== prev.cta && prev.cta) changes.push('CTA changed: "' + prev.cta + '" -> "' + curr.cta + '"');
const hasShift = changes.length > 0 ? 'true' : 'false';
return { has_positioning_shift: hasShift, change_details: changes.join('; ') || 'No changes detected', before_snapshot: JSON.stringify(prev), after_snapshot: JSON.stringify(curr), scan_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "current_headline", type: "string" },
            { name: "current_subhead", type: "string" },
            { name: "current_cta", type: "string" },
            { name: "prior_headline", type: "string" },
            { name: "prior_subhead", type: "string" },
            { name: "prior_cta", type: "string" },
          ],
          outputVariables: [
            { name: "has_positioning_shift", type: "string" },
            { name: "change_details", type: "string" },
            { name: "before_snapshot", type: "string" },
            { name: "after_snapshot", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Positioning shift detected?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "has_positioning_shift",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      aiNode(
        "5",
        "AI analyze strategic implications",
        1000,
        "You are a brand strategy analyst. A competitor changed their homepage messaging. Analyze what this positioning shift signals: market repositioning, new target audience, competitive response, or product pivot. Respond JSON: {implication_analysis: string, likely_motivation: string, target_audience_shift: string, recommended_response: string, urgency: string}",
        "Changes: {change_details}. Before: {before_snapshot}. After: {after_snapshot}. Date: {scan_date}.",
        [
          {
            field: "implication_analysis",
            as: "implication_analysis",
            type: "string",
          },
          {
            field: "likely_motivation",
            as: "likely_motivation",
            type: "string",
          },
          {
            field: "target_audience_shift",
            as: "target_audience_shift",
            type: "string",
          },
          {
            field: "recommended_response",
            as: "recommended_response",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),
      sheetsAppend(
        "6",
        "Archive messaging update",
        1240,
        "MessagingArchive!A:G",
        '[["{scan_date}","{change_details}","{before_snapshot}","{after_snapshot}","{implication_analysis}","{likely_motivation}","{urgency}"]]',
      ),
      slackNode(
        "7",
        "Alert strategy team",
        1480,
        "Messaging Shift Detected ({scan_date})\nChanges: {change_details}\nAnalysis: {implication_analysis}\nMotivation: {likely_motivation}\nAudience Shift: {target_audience_shift}\nRecommended Response: {recommended_response}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 291. OM05 FEATURE MATRIX BUILDER (7 nodes)
  // ===================================================================
  "OM05 Feature Matrix Builder": {
    summary:
      "Monthly competitor feature page scrape, taxonomy normalization via runCode, gap/change detection, AI whitespace analysis, matrix update in Sheets, product/strategy notification on Slack, and change highlights.",
    nodes: [
      trigger("Schedule: Monthly feature matrix update", "schedule", {
        schedule: "0 10 1 * *",
        howItStarts:
          "Runs on the 1st of every month to scrape competitor feature pages, normalize taxonomy, and update the competitive feature matrix",
        setup:
          "Configure ScrapingBee key. Set competitor feature page URLs. Set spreadsheet_id for feature matrix. Connect Slack webhook for product team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Scrape competitor feature pages", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url={competitor_features_url}&extract_rules={"features":{"selector":".feature-item,.feature-card","type":"list","output":{"name":{"selector":".feature-name,h3"},"description":{"selector":".feature-desc,p"},"category":{"selector":".feature-category"}}}}',
          headers: [],
          body: [],
          variables: [
            {
              name: "features",
              customName: "competitor_features",
              type: "string",
            },
          ],
        },
      }),
      n("3", "runCode", "Normalize taxonomy and detect changes", 520, {
        codeDetails: {
          language: "javascript",
          code: `const features = JSON.parse(inputs.competitor_features || '[]');
const prior = JSON.parse(inputs.prior_features || '[]');
const categories = {};
features.forEach(f => {
  const cat = f.category || 'Uncategorized';
  if (!categories[cat]) categories[cat] = [];
  categories[cat].push(f.name || 'unnamed');
});
const priorNames = prior.map(f => f.name || '');
const currNames = features.map(f => f.name || '');
const newFeatures = currNames.filter(n => n && !priorNames.includes(n));
const removedFeatures = priorNames.filter(n => n && !currNames.includes(n));
return { normalized_taxonomy: JSON.stringify(categories), new_features: JSON.stringify(newFeatures), removed_features: JSON.stringify(removedFeatures), new_count: newFeatures.length, removed_count: removedFeatures.length, total_features: features.length, scan_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "competitor_features", type: "string" },
            { name: "prior_features", type: "string" },
          ],
          outputVariables: [
            { name: "normalized_taxonomy", type: "string" },
            { name: "new_features", type: "string" },
            { name: "removed_features", type: "string" },
            { name: "new_count", type: "number" },
            { name: "removed_count", type: "number" },
            { name: "total_features", type: "number" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI whitespace and gap analysis",
        760,
        "You are a product strategist analyzing a competitive feature matrix. Given the normalized features, new additions, and removals, identify whitespace opportunities (features competitors lack), areas of feature parity, and differentiation opportunities. Respond JSON: {whitespace_opportunities: string, parity_areas: string, differentiation_plays: string, competitor_direction: string, priority_gaps: string, change_highlights: string}",
        "Features: {normalized_taxonomy}. New: {new_features}. Removed: {removed_features}. Total: {total_features}.",
        [
          {
            field: "whitespace_opportunities",
            as: "whitespace_opportunities",
            type: "string",
          },
          { field: "parity_areas", as: "parity_areas", type: "string" },
          {
            field: "differentiation_plays",
            as: "differentiation_plays",
            type: "string",
          },
          {
            field: "competitor_direction",
            as: "competitor_direction",
            type: "string",
          },
          { field: "priority_gaps", as: "priority_gaps", type: "string" },
          {
            field: "change_highlights",
            as: "change_highlights",
            type: "string",
          },
        ],
      ),
      sheetsAppend(
        "5",
        "Update feature matrix in Sheets",
        1000,
        "FeatureMatrix!A:H",
        '[["{scan_date}","{total_features}","{new_count}","{removed_count}","{whitespace_opportunities}","{parity_areas}","{differentiation_plays}","{priority_gaps}"]]',
      ),
      slackNode(
        "6",
        "Notify product and strategy",
        1240,
        "Feature Matrix Update ({scan_date})\nTotal: {total_features} | New: {new_count} | Removed: {removed_count}\nWhitespace: {whitespace_opportunities}\nDifferentiation: {differentiation_plays}\nCompetitor Direction: {competitor_direction}\nChanges: {change_highlights}",
      ),
      sheetsAppend(
        "7",
        "Log change highlights",
        1480,
        "FeatureChanges!A:E",
        '[["{scan_date}","{new_features}","{removed_features}","{change_highlights}","{competitor_direction}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 292. OM06 REVIEW SENTIMENT TRACKER (7 nodes)
  // ===================================================================
  "OM06 Review Sentiment Tracker": {
    summary:
      "Weekly multi-platform review pull, rating normalization via runCode, AI sentiment and theme analysis, trend signal condition, dashboard update in Sheets, sales/product brief on Slack, and switching-reason archive.",
    nodes: [
      trigger("Schedule: Weekly review scan", "schedule", {
        schedule: "0 9 * * 2",
        howItStarts:
          "Runs every Tuesday at 9 AM to pull competitor reviews across G2, Capterra, and Trustpilot, analyze sentiment trends, and extract switching signals",
        setup:
          "Configure G2/Capterra API or scraping credentials. Set spreadsheet_id for review dashboard. Connect Slack webhook for sales and product teams.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull reviews from G2", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://data.g2.com/api/v1/products/{competitor_g2_id}/reviews?page[size]=50&sort=-submitted_at",
          headers: [
            { key: "Authorization", value: "Token token={g2_api_token}" },
            { key: "Content-Type", value: "application/vnd.api+json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "g2_reviews", type: "string" },
            {
              name: "meta.record_count",
              customName: "review_count",
              type: "number",
            },
          ],
        },
      }),
      n("3", "runCode", "Normalize ratings across platforms", 520, {
        codeDetails: {
          language: "javascript",
          code: `const reviews = JSON.parse(inputs.g2_reviews || '[]');
const count = reviews.length || Number(inputs.review_count) || 0;
let totalRating = 0;
let lowRated = 0;
const themes = {};
reviews.forEach(r => {
  const rating = r.attributes ? r.attributes.star_rating : (r.star_rating || 3);
  totalRating += rating;
  if (rating <= 2) lowRated++;
});
const avgRating = count > 0 ? (totalRating / count).toFixed(2) : '0';
const lowRatedPct = count > 0 ? ((lowRated / count) * 100).toFixed(1) : '0';
const hasTrendSignal = (Number(lowRatedPct) > 20 || Number(avgRating) < 3.5) ? 'true' : 'false';
return { avg_rating: avgRating, total_reviews: count, low_rated_count: lowRated, low_rated_pct: lowRatedPct, has_trend_signal: hasTrendSignal, scan_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "g2_reviews", type: "string" },
            { name: "review_count", type: "number" },
          ],
          outputVariables: [
            { name: "avg_rating", type: "string" },
            { name: "total_reviews", type: "number" },
            { name: "low_rated_count", type: "number" },
            { name: "low_rated_pct", type: "string" },
            { name: "has_trend_signal", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI sentiment and theme analysis",
        760,
        "You are a competitive intelligence analyst. Analyze competitor reviews for sentiment trends, recurring complaint themes, praise themes, switching reasons (why users left), and emerging dissatisfaction signals. Respond JSON: {sentiment_trend: string, complaint_themes: string, praise_themes: string, switching_reasons: string, emerging_issues: string, sales_ammunition: string}",
        "Reviews: {g2_reviews}. Avg rating: {avg_rating}. Low-rated: {low_rated_count}/{total_reviews} ({low_rated_pct}%).",
        [
          { field: "sentiment_trend", as: "sentiment_trend", type: "string" },
          { field: "complaint_themes", as: "complaint_themes", type: "string" },
          { field: "praise_themes", as: "praise_themes", type: "string" },
          {
            field: "switching_reasons",
            as: "switching_reasons",
            type: "string",
          },
          { field: "emerging_issues", as: "emerging_issues", type: "string" },
          { field: "sales_ammunition", as: "sales_ammunition", type: "string" },
        ],
      ),
      n("5", "condition", "Negative trend signal detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_trend_signal", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Update review dashboard",
        1240,
        "ReviewSentiment!A:H",
        '[["{scan_date}","{avg_rating}","{total_reviews}","{low_rated_pct}","{sentiment_trend}","{complaint_themes}","{switching_reasons}","{sales_ammunition}"]]',
      ),
      slackNode(
        "7",
        "Sales and product brief",
        1480,
        "Review Sentiment Report ({scan_date})\nAvg Rating: {avg_rating} | Reviews: {total_reviews} | Low-rated: {low_rated_pct}%\nSentiment: {sentiment_trend}\nComplaints: {complaint_themes}\nSwitching Reasons: {switching_reasons}\nSales Ammo: {sales_ammunition}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 293. OM07 COMPETITOR EMAIL DIGEST (7 nodes)
  // ===================================================================
  "OM07 Competitor Email Digest": {
    summary:
      "Newsletter inbox scan, subject/body/CTA parsing via runCode, AI cross-competitor clustering, major announcement condition, email archive in Sheets, strategy/lifecycle digest on Slack, and pattern tracking.",
    nodes: [
      trigger("Schedule: Daily competitor email scan", "schedule", {
        schedule: "0 10 * * 1-5",
        howItStarts:
          "Runs daily at 10 AM on weekdays to scan the competitor newsletter inbox, parse emails, cluster themes, and alert on major announcements",
        setup:
          "Configure IMAP/API access to the competitor newsletter inbox (e.g., via Mailpit or dedicated inbox). Set spreadsheet_id for email archive. Connect Slack webhook.",
        inputFields: [],
      }),
      n("2", "hitApi", "Scan newsletter inbox via API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.mailpit.io/v1/messages?limit=20&since={yesterday_date}T00:00:00Z",
          headers: [{ key: "Authorization", value: "Bearer {mailpit_key}" }],
          body: [],
          variables: [
            { name: "messages", customName: "inbox_messages", type: "string" },
            { name: "total", customName: "message_count", type: "number" },
          ],
        },
      }),
      n("3", "runCode", "Parse subject, body, and CTAs", 520, {
        codeDetails: {
          language: "javascript",
          code: `const messages = JSON.parse(inputs.inbox_messages || '[]');
const parsed = messages.map(m => ({
  from: m.from || m.sender || '',
  subject: m.subject || '',
  preview: (m.body || m.snippet || '').substring(0, 500),
  date: m.date || m.created_at || '',
  ctas: (m.body || '').match(/https?:\\/\\/[^\\s"']+/g) || [],
}));
const competitors = {};
parsed.forEach(p => {
  const sender = p.from.split('@')[1] || p.from;
  if (!competitors[sender]) competitors[sender] = [];
  competitors[sender].push(p.subject);
});
return { parsed_emails: JSON.stringify(parsed.slice(0, 15)), competitor_breakdown: JSON.stringify(competitors), email_count: parsed.length, scan_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [{ name: "inbox_messages", type: "string" }],
          outputVariables: [
            { name: "parsed_emails", type: "string" },
            { name: "competitor_breakdown", type: "string" },
            { name: "email_count", type: "number" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI cluster themes across competitors",
        760,
        "You are a lifecycle marketing analyst tracking competitor email strategies. Analyze the parsed competitor emails for: messaging themes, announcement types (product launch, pricing change, event, content), urgency tactics, personalization approaches, and cross-competitor patterns. Flag any major announcements. Respond JSON: {theme_clusters: string, major_announcements: string, has_major_announcement: string, urgency_tactics: string, personalization_patterns: string, cross_competitor_insights: string}",
        "Emails: {parsed_emails}. Competitors: {competitor_breakdown}. Count: {email_count}.",
        [
          { field: "theme_clusters", as: "theme_clusters", type: "string" },
          {
            field: "major_announcements",
            as: "major_announcements",
            type: "string",
          },
          {
            field: "has_major_announcement",
            as: "has_major_announcement",
            type: "string",
          },
          { field: "urgency_tactics", as: "urgency_tactics", type: "string" },
          {
            field: "personalization_patterns",
            as: "personalization_patterns",
            type: "string",
          },
          {
            field: "cross_competitor_insights",
            as: "cross_competitor_insights",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "Major announcement detected?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "has_major_announcement",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Archive emails and categories",
        1240,
        "CompetitorEmails!A:G",
        '[["{scan_date}","{email_count}","{competitor_breakdown}","{theme_clusters}","{major_announcements}","{urgency_tactics}","{personalization_patterns}"]]',
      ),
      slackNode(
        "7",
        "Strategy and lifecycle digest",
        1480,
        "Competitor Email Digest ({scan_date})\nEmails scanned: {email_count}\nTheme Clusters: {theme_clusters}\nMajor Announcements: {major_announcements}\nUrgency Tactics: {urgency_tactics}\nCross-competitor: {cross_competitor_insights}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 294. OM09 LANDING PAGE ARCHIVE (7 nodes)
  // ===================================================================
  "OM09 Landing Page Archive": {
    summary:
      "Scheduled URL scrape for competitor landing pages, full content capture, prior version diff via runCode, change significance condition, archive storage in Sheets, CRO/strategy alert on Slack, and diff metadata log.",
    nodes: [
      trigger("Schedule: Weekly landing page archive", "schedule", {
        schedule: "0 7 * * 3",
        howItStarts:
          "Runs every Wednesday at 7 AM to archive competitor landing pages, detect changes, and alert on significant modifications",
        setup:
          "Configure ScrapingBee key. Set list of competitor landing page URLs. Set spreadsheet_id for archive. Connect Slack webhook for CRO team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Scrape landing page content", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url={landing_page_url}&render_js=true&extract_rules={"headline":{"selector":"h1"},"subheadline":{"selector":".hero-subtitle,h2"},"cta":{"selector":".cta-button,button.primary"},"social_proof":{"selector":".testimonial,.social-proof"},"body_text":{"selector":"main","output":"text"}}',
          headers: [],
          body: [],
          variables: [
            { name: "headline", customName: "page_headline", type: "string" },
            {
              name: "subheadline",
              customName: "page_subheadline",
              type: "string",
            },
            { name: "cta", customName: "page_cta", type: "string" },
            {
              name: "social_proof",
              customName: "page_social_proof",
              type: "string",
            },
            { name: "body_text", customName: "page_body", type: "string" },
          ],
        },
      }),
      n("3", "runCode", "Diff against prior version", 520, {
        codeDetails: {
          language: "javascript",
          code: `const current = { headline: inputs.page_headline || '', cta: inputs.page_cta || '', body: (inputs.page_body || '').substring(0, 2000) };
const prior = { headline: inputs.prior_headline || '', cta: inputs.prior_cta || '', body: (inputs.prior_body || '').substring(0, 2000) };
const changes = [];
if (current.headline !== prior.headline && prior.headline) changes.push('Headline changed');
if (current.cta !== prior.cta && prior.cta) changes.push('CTA changed');
const bodyDiffPct = prior.body ? Math.abs(current.body.length - prior.body.length) / Math.max(prior.body.length, 1) * 100 : 0;
if (bodyDiffPct > 10) changes.push('Body content changed by ' + bodyDiffPct.toFixed(0) + '%');
const isSignificant = changes.length > 0 ? 'true' : 'false';
return { is_significant: isSignificant, change_list: changes.join('; ') || 'No changes', body_diff_pct: bodyDiffPct.toFixed(1), change_count: changes.length, archive_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "page_headline", type: "string" },
            { name: "page_cta", type: "string" },
            { name: "page_body", type: "string" },
            { name: "prior_headline", type: "string" },
            { name: "prior_cta", type: "string" },
            { name: "prior_body", type: "string" },
          ],
          outputVariables: [
            { name: "is_significant", type: "string" },
            { name: "change_list", type: "string" },
            { name: "body_diff_pct", type: "string" },
            { name: "change_count", type: "number" },
            { name: "archive_date", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Significant changes detected?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_significant", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsAppend(
        "5",
        "Archive page snapshot",
        1000,
        "LandingPageArchive!A:H",
        '[["{archive_date}","{landing_page_url}","{page_headline}","{page_subheadline}","{page_cta}","{change_list}","{body_diff_pct}","{is_significant}"]]',
      ),
      slackNode(
        "6",
        "Alert CRO and strategy team",
        1240,
        "Landing Page Change Detected ({archive_date})\nURL: {landing_page_url}\nChanges: {change_list}\nBody diff: {body_diff_pct}%\nNew Headline: {page_headline}\nNew CTA: {page_cta}",
      ),
      sheetsAppend(
        "7",
        "Log diff metadata",
        1480,
        "LandingPageDiffs!A:E",
        '[["{archive_date}","{landing_page_url}","{change_count}","{change_list}","{body_diff_pct}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("6", "5"),
      e("5", "7"),
    ],
  },

  // ===================================================================
  // 295. OM10 MARKET CATEGORY MAP (7 nodes)
  // ===================================================================
  "OM10 Market Category Map": {
    summary:
      "Quarterly competitor and funding data pull, AI landscape segmentation, whitespace analysis, market map publication in Sheets, leadership notification on Slack, and new entrant tracking.",
    nodes: [
      trigger("Schedule: Quarterly market map update", "schedule", {
        schedule: "0 10 1 1,4,7,10 *",
        howItStarts:
          "Runs quarterly to compile competitor data, segment the market landscape, and update the competitive market map",
        setup:
          "Configure Crunchbase API key for funding data. Set spreadsheet_id for market map. Connect Slack webhook for leadership.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull competitor and funding data", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.crunchbase.com/api/v4/searches/organizations",
          headers: [
            { key: "X-cb-user-key", value: "{crunchbase_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "field_ids",
              value:
                '["identifier","short_description","categories","funding_total","last_funding_type","num_employees_enum","founded_on","location_identifiers"]',
            },
            {
              key: "query",
              value:
                '[{"type":"predicate","field_id":"categories","operator_id":"includes","values":["{market_category}"]}]',
            },
            { key: "limit", value: "50" },
          ],
          variables: [
            { name: "entities", customName: "market_entities", type: "string" },
            { name: "count", customName: "entity_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI segment market landscape",
        520,
        "You are a market analyst. Given the competitor data with funding, size, and category information, segment the market into tiers (leaders, challengers, niche players, emerging), identify market sub-categories, and map competitive dynamics. Respond JSON: {market_segments: string, tier_distribution: string, sub_categories: string, competitive_dynamics: string, market_maturity: string}",
        "Entities: {market_entities}. Count: {entity_count}.",
        [
          { field: "market_segments", as: "market_segments", type: "string" },
          {
            field: "tier_distribution",
            as: "tier_distribution",
            type: "string",
          },
          { field: "sub_categories", as: "sub_categories", type: "string" },
          {
            field: "competitive_dynamics",
            as: "competitive_dynamics",
            type: "string",
          },
          { field: "market_maturity", as: "market_maturity", type: "string" },
        ],
      ),
      aiNode(
        "4",
        "AI whitespace and new entrant analysis",
        760,
        "You are a strategic advisor. Given the market segmentation and competitive dynamics, identify whitespace opportunities, underserved segments, likely new entrants, and emerging threats. Respond JSON: {whitespace_areas: string, underserved_segments: string, likely_new_entrants: string, emerging_threats: string, strategic_recommendations: string, new_entrant_count: number}",
        "Segments: {market_segments}. Tiers: {tier_distribution}. Sub-categories: {sub_categories}. Dynamics: {competitive_dynamics}. Maturity: {market_maturity}.",
        [
          { field: "whitespace_areas", as: "whitespace_areas", type: "string" },
          {
            field: "underserved_segments",
            as: "underserved_segments",
            type: "string",
          },
          {
            field: "likely_new_entrants",
            as: "likely_new_entrants",
            type: "string",
          },
          { field: "emerging_threats", as: "emerging_threats", type: "string" },
          {
            field: "strategic_recommendations",
            as: "strategic_recommendations",
            type: "string",
          },
          {
            field: "new_entrant_count",
            as: "new_entrant_count",
            type: "number",
          },
        ],
      ),
      sheetsAppend(
        "5",
        "Publish market map to Sheets",
        1000,
        "MarketMap!A:H",
        '[["{scan_date}","{entity_count}","{market_segments}","{tier_distribution}","{whitespace_areas}","{underserved_segments}","{emerging_threats}","{strategic_recommendations}"]]',
      ),
      slackNode(
        "6",
        "Notify leadership",
        1240,
        "Quarterly Market Map Update\nEntities tracked: {entity_count} | Maturity: {market_maturity}\nSegments: {market_segments}\nWhitespace: {whitespace_areas}\nNew Entrants: {likely_new_entrants} ({new_entrant_count})\nRecommendations: {strategic_recommendations}",
      ),
      sheetsAppend(
        "7",
        "Track new entrants",
        1480,
        "NewEntrants!A:D",
        '[["{scan_date}","{likely_new_entrants}","{new_entrant_count}","{emerging_threats}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 296. OM11 FOUNDER TONE ANALYSIS (7 nodes)
  // ===================================================================
  "OM11 Founder Tone Analysis": {
    summary:
      "Bi-weekly founder content pull via SerpAPI, AI tone/confidence/narrative analysis, strategic hint extraction, tracker update in Sheets, competitive intel notification on Slack, and quote archive.",
    nodes: [
      trigger("Schedule: Bi-weekly founder content scan", "schedule", {
        schedule: "0 10 1,15 * *",
        howItStarts:
          "Runs on the 1st and 15th of each month to scan founder public communications for tone shifts, confidence signals, and strategic hints",
        setup:
          "Configure SerpAPI key. Set founder names and company names to track. Set spreadsheet_id for tracker. Connect Slack webhook for intel team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull founder content via SerpAPI", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={founder_name}+{company_name}&tbm=nws&num=20&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "news_results",
              customName: "founder_content",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "result_count",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze tone and confidence",
        520,
        "You are a communications analyst specializing in executive speech patterns. Analyze the founder's recent public communications for: overall tone (bullish, cautious, defensive, visionary), confidence level (1-10), narrative themes, strategic hints (hidden in language choices), and any notable shifts from typical messaging. Respond JSON: {tone: string, confidence_level: number, narrative_themes: string, strategic_hints: string, notable_shifts: string, key_quotes: string}",
        "Founder: {founder_name}. Company: {company_name}. Content: {founder_content}. Result count: {result_count}.",
        [
          { field: "tone", as: "founder_tone", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "number" },
          { field: "narrative_themes", as: "narrative_themes", type: "string" },
          { field: "strategic_hints", as: "strategic_hints", type: "string" },
          { field: "notable_shifts", as: "notable_shifts", type: "string" },
          { field: "key_quotes", as: "key_quotes", type: "string" },
        ],
      ),
      aiNode(
        "4",
        "AI extract strategic implications",
        760,
        "You are a competitive strategist. Given the founder tone analysis, infer what the tone and narrative shifts mean for their company's direction: potential product pivots, fundraising signals, market expansion plans, or defensive posturing. Respond JSON: {strategic_inference: string, pivot_signals: string, fundraising_signals: string, market_expansion_clues: string, defensive_indicators: string}",
        "Tone: {founder_tone}. Confidence: {confidence_level}/10. Themes: {narrative_themes}. Hints: {strategic_hints}. Shifts: {notable_shifts}.",
        [
          {
            field: "strategic_inference",
            as: "strategic_inference",
            type: "string",
          },
          { field: "pivot_signals", as: "pivot_signals", type: "string" },
          {
            field: "fundraising_signals",
            as: "fundraising_signals",
            type: "string",
          },
          {
            field: "market_expansion_clues",
            as: "market_expansion_clues",
            type: "string",
          },
          {
            field: "defensive_indicators",
            as: "defensive_indicators",
            type: "string",
          },
        ],
      ),
      sheetsAppend(
        "5",
        "Update founder tracker",
        1000,
        "FounderTone!A:H",
        '[["{scan_date}","{founder_name}","{founder_tone}","{confidence_level}","{narrative_themes}","{strategic_hints}","{strategic_inference}","{pivot_signals}"]]',
      ),
      slackNode(
        "6",
        "Competitive intel notification",
        1240,
        "Founder Tone Analysis: {founder_name}\nTone: {founder_tone} | Confidence: {confidence_level}/10\nThemes: {narrative_themes}\nStrategic Hints: {strategic_hints}\nInference: {strategic_inference}\nKey Quotes: {key_quotes}",
      ),
      sheetsAppend(
        "7",
        "Archive key quotes",
        1480,
        "FounderQuotes!A:D",
        '[["{scan_date}","{founder_name}","{key_quotes}","{founder_tone}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 297. OM12 OFFER MATRIX TRACKER (7 nodes)
  // ===================================================================
  "OM12 Offer Matrix Tracker": {
    summary:
      "Weekly competitor offer scrape, promo email parsing via runCode, AI discount depth comparison, new offer behavior condition, offer matrix update in Sheets, sales/pricing alert on Slack, and counter-positioning notes.",
    nodes: [
      trigger("Schedule: Weekly offer matrix scan", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday at 9 AM to scan competitor promotional offers, parse discount structures, and update the competitive offer matrix",
        setup:
          "Configure ScrapingBee key for offer page scraping. Set competitor pricing/offers URLs. Set spreadsheet_id for offer matrix. Connect Slack webhook.",
        inputFields: [],
      }),
      n("2", "hitApi", "Scrape competitor offer pages", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url={competitor_offers_url}&extract_rules={"offers":{"selector":".offer,.promo,.deal","type":"list","output":{"title":{"selector":".offer-title,h3"},"discount":{"selector":".discount,.price-cut"},"terms":{"selector":".terms,.conditions"},"expiry":{"selector":".expiry,.deadline"}}}}',
          headers: [],
          body: [],
          variables: [
            { name: "offers", customName: "current_offers", type: "string" },
          ],
        },
      }),
      n("3", "runCode", "Parse promo structures and compare", 520, {
        codeDetails: {
          language: "javascript",
          code: `const offers = JSON.parse(inputs.current_offers || '[]');
const priorOffers = JSON.parse(inputs.prior_offers || '[]');
const parsed = offers.map(o => ({
  title: o.title || '',
  discount: o.discount || '',
  terms: o.terms || '',
  expiry: o.expiry || '',
}));
const priorTitles = priorOffers.map(o => o.title || '');
const newOffers = parsed.filter(o => o.title && !priorTitles.includes(o.title));
const hasNewOffer = newOffers.length > 0 ? 'true' : 'false';
return { parsed_offers: JSON.stringify(parsed), new_offers: JSON.stringify(newOffers), new_offer_count: newOffers.length, total_active_offers: parsed.length, has_new_offer: hasNewOffer, scan_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "current_offers", type: "string" },
            { name: "prior_offers", type: "string" },
          ],
          outputVariables: [
            { name: "parsed_offers", type: "string" },
            { name: "new_offers", type: "string" },
            { name: "new_offer_count", type: "number" },
            { name: "total_active_offers", type: "number" },
            { name: "has_new_offer", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI compare discount depth and strategy",
        760,
        "You are a pricing strategist analyzing competitor promotional offers. Compare discount depths, offer structures, and promotional strategies. Identify aggressive moves, seasonal patterns, and recommend counter-positioning. Respond JSON: {discount_analysis: string, strategy_type: string, aggressiveness: string, seasonal_pattern: string, counter_positioning: string, sales_talking_points: string}",
        "Current offers: {parsed_offers}. New offers: {new_offers}. Total active: {total_active_offers}.",
        [
          {
            field: "discount_analysis",
            as: "discount_analysis",
            type: "string",
          },
          { field: "strategy_type", as: "strategy_type", type: "string" },
          { field: "aggressiveness", as: "aggressiveness", type: "string" },
          { field: "seasonal_pattern", as: "seasonal_pattern", type: "string" },
          {
            field: "counter_positioning",
            as: "counter_positioning",
            type: "string",
          },
          {
            field: "sales_talking_points",
            as: "sales_talking_points",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "New offer behavior detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_new_offer", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Update offer matrix",
        1240,
        "OfferMatrix!A:H",
        '[["{scan_date}","{total_active_offers}","{new_offer_count}","{discount_analysis}","{strategy_type}","{aggressiveness}","{counter_positioning}","{sales_talking_points}"]]',
      ),
      slackNode(
        "7",
        "Sales and pricing alert",
        1480,
        "Offer Matrix Update ({scan_date})\nActive Offers: {total_active_offers} | New: {new_offer_count}\nDiscount Analysis: {discount_analysis}\nStrategy: {strategy_type} | Aggressiveness: {aggressiveness}\nCounter-positioning: {counter_positioning}\nSales Talking Points: {sales_talking_points}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 298. OM13 OBJECTION MINING FROM REVIEWS (7 nodes)
  // ===================================================================
  "OM13 Objection Mining from Reviews": {
    summary:
      "Low-rated review pull, AI complaint extraction and clustering, severity ranking, battle card generation, objection database update in Sheets, sales enablement notification on Slack, and emerging objection alert.",
    nodes: [
      trigger("Schedule: Weekly objection mining", "schedule", {
        schedule: "0 8 * * 3",
        howItStarts:
          "Runs every Wednesday at 8 AM to mine low-rated competitor reviews for objections, cluster complaints, and generate sales battle cards",
        setup:
          "Configure G2/Capterra API for review access. Set spreadsheet_id for objection database. Connect Slack webhook for sales enablement team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull low-rated reviews", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://data.g2.com/api/v1/products/{competitor_g2_id}/reviews?page[size]=50&filter[star_rating]=1,2,3&sort=-submitted_at",
          headers: [
            { key: "Authorization", value: "Token token={g2_api_token}" },
            { key: "Content-Type", value: "application/vnd.api+json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "low_reviews", type: "string" },
            {
              name: "meta.record_count",
              customName: "review_count",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI extract and cluster complaints",
        520,
        "You are a competitive intelligence analyst. Extract specific complaints from low-rated reviews, cluster them into objection categories (e.g., pricing, UX, support, reliability, missing features, onboarding), rank by severity and frequency, and identify emerging new objections. Respond JSON: {objection_clusters: string, top_objections: string, severity_ranking: string, emerging_objections: string, frequency_distribution: string, total_objections_found: number}",
        "Low-rated reviews: {low_reviews}. Count: {review_count}.",
        [
          {
            field: "objection_clusters",
            as: "objection_clusters",
            type: "string",
          },
          { field: "top_objections", as: "top_objections", type: "string" },
          { field: "severity_ranking", as: "severity_ranking", type: "string" },
          {
            field: "emerging_objections",
            as: "emerging_objections",
            type: "string",
          },
          {
            field: "frequency_distribution",
            as: "frequency_distribution",
            type: "string",
          },
          {
            field: "total_objections_found",
            as: "total_objections_found",
            type: "number",
          },
        ],
      ),
      aiNode(
        "4",
        "AI generate battle cards",
        760,
        "You are a sales enablement expert. Based on the competitor objection clusters and severity ranking, generate battle card content: rebuttal scripts, proof points, competitive positioning statements, and talk tracks for each major objection. Respond JSON: {battle_cards: string, rebuttal_scripts: string, proof_points: string, positioning_statements: string, talk_tracks: string}",
        "Objections: {objection_clusters}. Top: {top_objections}. Severity: {severity_ranking}. Emerging: {emerging_objections}.",
        [
          { field: "battle_cards", as: "battle_cards", type: "string" },
          { field: "rebuttal_scripts", as: "rebuttal_scripts", type: "string" },
          { field: "proof_points", as: "proof_points", type: "string" },
          {
            field: "positioning_statements",
            as: "positioning_statements",
            type: "string",
          },
          { field: "talk_tracks", as: "talk_tracks", type: "string" },
        ],
      ),
      sheetsAppend(
        "5",
        "Update objection database",
        1000,
        "ObjectionDB!A:H",
        '[["{scan_date}","{total_objections_found}","{objection_clusters}","{severity_ranking}","{emerging_objections}","{battle_cards}","{rebuttal_scripts}","{positioning_statements}"]]',
      ),
      slackNode(
        "6",
        "Sales enablement notification",
        1240,
        "Objection Mining Update ({scan_date})\nObjections found: {total_objections_found}\nTop Objections: {top_objections}\nEmerging: {emerging_objections}\nBattle Cards: {battle_cards}\nKey Rebuttals: {rebuttal_scripts}",
      ),
      slackNode(
        "7",
        "Emerging objection alert",
        1480,
        "NEW EMERGING OBJECTIONS DETECTED\n{emerging_objections}\nSeverity: {severity_ranking}\nRecommended talk track: {talk_tracks}",
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 299. OM14 FEATURE ROLLOUT MONITOR (7 nodes)
  // ===================================================================
  "OM14 Feature Rollout Monitor": {
    summary:
      "Daily changelog scrape, AI feature extraction, impact scoring condition, rollout tracker update in Sheets, product alert on Slack, gap/response notes, and archive.",
    nodes: [
      trigger("Schedule: Daily changelog scan", "schedule", {
        schedule: "0 8 * * 1-5",
        howItStarts:
          "Runs daily at 8 AM on weekdays to scan competitor changelogs and release notes for new feature rollouts",
        setup:
          "Configure ScrapingBee key. Set competitor changelog URLs. Set spreadsheet_id for rollout tracker. Connect Slack webhook for product team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Scrape changelog and release notes", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url={competitor_changelog_url}&extract_rules={"entries":{"selector":".changelog-entry,.release-note","type":"list","output":{"date":{"selector":".date"},"title":{"selector":"h3,.entry-title"},"description":{"selector":".description,p"}}}}',
          headers: [],
          body: [],
          variables: [
            {
              name: "entries",
              customName: "changelog_entries",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI extract features and score impact",
        520,
        "You are a product analyst monitoring competitor releases. Extract individual features from changelog entries, classify each (new feature, improvement, bug fix, API change), and score impact (1-10) based on competitive significance. Respond JSON: {features_extracted: string, feature_count: number, high_impact_features: string, high_impact_count: number, categories: string, competitive_response_needed: string}",
        "Changelog entries: {changelog_entries}.",
        [
          {
            field: "features_extracted",
            as: "features_extracted",
            type: "string",
          },
          { field: "feature_count", as: "feature_count", type: "number" },
          {
            field: "high_impact_features",
            as: "high_impact_features",
            type: "string",
          },
          {
            field: "high_impact_count",
            as: "high_impact_count",
            type: "number",
          },
          { field: "categories", as: "feature_categories", type: "string" },
          {
            field: "competitive_response_needed",
            as: "competitive_response_needed",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "High-impact features found?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "high_impact_count",
              operator: "greater_than",
              value: "0",
            },
          ],
        },
      }),
      sheetsAppend(
        "5",
        "Update rollout tracker",
        1000,
        "FeatureRollout!A:G",
        '[["{scan_date}","{feature_count}","{high_impact_count}","{features_extracted}","{feature_categories}","{high_impact_features}","{competitive_response_needed}"]]',
      ),
      slackNode(
        "6",
        "Product alert on Slack",
        1240,
        "Feature Rollout Detected ({scan_date})\nFeatures: {feature_count} | High Impact: {high_impact_count}\nHigh Impact: {high_impact_features}\nCategories: {feature_categories}\nResponse Needed: {competitive_response_needed}",
      ),
      sheetsAppend(
        "7",
        "Archive with gap notes",
        1480,
        "FeatureRolloutArchive!A:D",
        '[["{scan_date}","{high_impact_features}","{competitive_response_needed}","{feature_categories}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("6", "5"),
      e("5", "7"),
    ],
  },

  // ===================================================================
  // 300. OM15 HIRING + PRICING COMBINED DETECTOR (7 nodes)
  // ===================================================================
  "OM15 Hiring + Pricing Combined Detector": {
    summary:
      "Weekly cross-signal intelligence combining job page and pricing diffs via runCode, AI pivot analysis on combined signals, confidence condition, cross-signal dashboard in Sheets, leadership alert on Slack, and evidence archive.",
    nodes: [
      trigger("Schedule: Weekly cross-signal analysis", "schedule", {
        schedule: "0 11 * * 5",
        howItStarts:
          "Runs every Friday at 11 AM to combine hiring signals with pricing changes for cross-signal competitive pivot detection",
        setup:
          "Configure access to hiring tracker and pricing watch spreadsheets. Set spreadsheet_id for cross-signal dashboard. Connect Slack webhook for leadership.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull latest hiring and pricing signals", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/HiringTracker!A:H?majorDimension=ROWS",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            { name: "values", customName: "hiring_data", type: "string" },
          ],
        },
      }),
      n("3", "runCode", "Merge hiring + pricing signals", 520, {
        codeDetails: {
          language: "javascript",
          code: `const hiringRaw = inputs.hiring_data || '[]';
const pricingRaw = inputs.pricing_data || '[]';
let hiringSignals = 'No recent hiring changes';
let pricingSignals = 'No recent pricing changes';
try {
  const hiring = JSON.parse(hiringRaw);
  if (Array.isArray(hiring) && hiring.length > 1) {
    const latest = hiring[hiring.length - 1];
    hiringSignals = 'New roles: ' + (latest[2] || '0') + ', Closed: ' + (latest[3] || '0') + ', Expansion: ' + (latest[5] || 'unknown');
  }
} catch(e) {}
try {
  const pricing = JSON.parse(pricingRaw);
  if (Array.isArray(pricing) && pricing.length > 1) {
    const latest = pricing[pricing.length - 1];
    pricingSignals = 'Changes: ' + (latest[1] || 'none') + ', Impact: ' + (latest[2] || 'unknown');
  }
} catch(e) {}
const combinedStrength = (hiringSignals !== 'No recent hiring changes' && pricingSignals !== 'No recent pricing changes') ? 'strong' : 'weak';
return { hiring_summary: hiringSignals, pricing_summary: pricingSignals, signal_strength: combinedStrength, high_confidence: combinedStrength === 'strong' ? 'true' : 'false', analysis_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "hiring_data", type: "string" },
            { name: "pricing_data", type: "string" },
          ],
          outputVariables: [
            { name: "hiring_summary", type: "string" },
            { name: "pricing_summary", type: "string" },
            { name: "signal_strength", type: "string" },
            { name: "high_confidence", type: "string" },
            { name: "analysis_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI combined pivot analysis",
        760,
        "You are a senior competitive strategist. When hiring changes and pricing changes happen simultaneously at a competitor, it often signals a strategic pivot. Analyze the combined signals for: pivot direction, market repositioning, revenue model shifts, and strategic intent. Respond JSON: {pivot_analysis: string, pivot_direction: string, market_repositioning: string, revenue_model_shift: string, strategic_intent: string, confidence: string, recommended_actions: string}",
        "Hiring signals: {hiring_summary}. Pricing signals: {pricing_summary}. Signal strength: {signal_strength}. Date: {analysis_date}.",
        [
          { field: "pivot_analysis", as: "pivot_analysis", type: "string" },
          { field: "pivot_direction", as: "pivot_direction", type: "string" },
          {
            field: "market_repositioning",
            as: "market_repositioning",
            type: "string",
          },
          {
            field: "revenue_model_shift",
            as: "revenue_model_shift",
            type: "string",
          },
          { field: "strategic_intent", as: "strategic_intent", type: "string" },
          { field: "confidence", as: "analysis_confidence", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "High-confidence cross-signal?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "high_confidence", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Update cross-signal dashboard",
        1240,
        "CrossSignal!A:H",
        '[["{analysis_date}","{signal_strength}","{hiring_summary}","{pricing_summary}","{pivot_analysis}","{pivot_direction}","{analysis_confidence}","{recommended_actions}"]]',
      ),
      slackNode(
        "7",
        "Leadership alert",
        1480,
        "Cross-Signal Competitive Alert ({analysis_date})\nSignal Strength: {signal_strength} | Confidence: {analysis_confidence}\nHiring: {hiring_summary}\nPricing: {pricing_summary}\nPivot Analysis: {pivot_analysis}\nDirection: {pivot_direction}\nRecommended Actions: {recommended_actions}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 301. OM16 EVENT SCANNER (7 nodes)
  // ===================================================================
  "OM16 Event Scanner": {
    summary:
      "Weekly event directory search, competitor presence scan, AI relevance scoring, must-attend condition, event calendar update in Sheets, marketing/partnerships notification on Slack, and deadline tracking.",
    nodes: [
      trigger("Schedule: Weekly event scan", "schedule", {
        schedule: "0 10 * * 2",
        howItStarts:
          "Runs every Tuesday at 10 AM to scan industry event directories for upcoming events, detect competitor presence, and identify must-attend opportunities",
        setup:
          "Configure SerpAPI key for event search. Set industry keywords. Set spreadsheet_id for event calendar. Connect Slack webhook for marketing/partnerships.",
        inputFields: [],
      }),
      n("2", "hitApi", "Search event directories", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={industry_keyword}+conference+2024&tbm=nws&num=30&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "news_results",
              customName: "event_results",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "result_count",
              type: "number",
            },
          ],
        },
      }),
      n("3", "hitApi", "Scan for competitor presence", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={competitor_names}+conference+sponsor+speaker+2024&num=20&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "competitor_event_presence",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "AI score relevance and identify must-attends",
        760,
        "You are an events strategist. Analyze event search results and competitor presence data to: identify relevant industry events, score each for strategic value (1-10), detect where competitors are sponsoring/speaking, and flag must-attend events. Respond JSON: {events_found: string, must_attend_events: string, must_attend_count: number, competitor_presence_map: string, strategic_value_avg: number, deadline_alerts: string}",
        "Events: {event_results}. Competitor presence: {competitor_event_presence}. Industry: {industry_keyword}.",
        [
          { field: "events_found", as: "events_found", type: "string" },
          {
            field: "must_attend_events",
            as: "must_attend_events",
            type: "string",
          },
          {
            field: "must_attend_count",
            as: "must_attend_count",
            type: "number",
          },
          {
            field: "competitor_presence_map",
            as: "competitor_presence_map",
            type: "string",
          },
          {
            field: "strategic_value_avg",
            as: "strategic_value_avg",
            type: "number",
          },
          { field: "deadline_alerts", as: "deadline_alerts", type: "string" },
        ],
      ),
      n("5", "condition", "Must-attend events found?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "must_attend_count",
              operator: "greater_than",
              value: "0",
            },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Update event calendar",
        1240,
        "EventCalendar!A:G",
        '[["{scan_date}","{events_found}","{must_attend_count}","{must_attend_events}","{competitor_presence_map}","{strategic_value_avg}","{deadline_alerts}"]]',
      ),
      slackNode(
        "7",
        "Notify marketing and partnerships",
        1480,
        "Event Scanner Update ({scan_date})\nMust-Attend Events: {must_attend_count}\n{must_attend_events}\nCompetitor Presence: {competitor_presence_map}\nDeadlines: {deadline_alerts}\nAvg Strategic Value: {strategic_value_avg}/10",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 302. OM17 EVENT ATTENDEE BRIEF (8 nodes)
  // ===================================================================
  "OM17 Event Attendee Brief": {
    summary:
      "Attendee list intake, Clearbit enrichment, CRM cross-reference, AI priority ranking, conversation starter generation, event brief published to Sheets, pre-event outreach email, and meeting tracking.",
    nodes: [
      trigger("Webhook: Attendee list uploaded", "webhook_app", {
        source: "Event Platform / CSV Upload",
        event: "attendee_list.uploaded",
        howItStarts:
          "An event attendee list is uploaded, triggering enrichment, prioritization, and pre-event outreach preparation",
        setup:
          "Connect your event platform or internal tool to POST the attendee list (names, companies, emails) to the workflow URL.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract attendee data",
        280,
        "Read this attendee list payload. Extract all attendees with their names, companies, titles, and emails regardless of format. Respond JSON: {attendees: string, attendee_count: number, event_name: string, event_date: string}",
        "{webhook_payload}",
        [
          { field: "attendees", as: "attendee_list", type: "string" },
          { field: "attendee_count", as: "attendee_count", type: "number" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_date", as: "event_date", type: "string" },
        ],
      ),
      n("3", "hitApi", "Clearbit enrichment (batch)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={first_attendee_domain}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          body: [],
          variables: [
            { name: "name", customName: "enriched_company", type: "string" },
            {
              name: "metrics.employees",
              customName: "company_size",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "revenue",
              type: "string",
            },
          ],
        },
      }),
      n("4", "hitApi", "CRM cross-reference via HubSpot", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"IN","values":["{attendee_emails}"]}]}]',
            },
          ],
          variables: [
            { name: "results", customName: "crm_matches", type: "string" },
            { name: "total", customName: "crm_match_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "5",
        "AI prioritize and generate conversation starters",
        1000,
        "You are an event networking strategist. Given the enriched attendee list and CRM matches, rank attendees by priority (based on company size, industry fit, existing relationship), and generate personalized conversation starters for the top 15. Respond JSON: {priority_rankings: string, top_targets: string, conversation_starters: string, existing_relationships: string, new_prospect_count: number}",
        "Attendees: {attendee_list}. CRM matches: {crm_matches} ({crm_match_count}). Enriched: {enriched_company}, {industry}, {company_size} employees.",
        [
          {
            field: "priority_rankings",
            as: "priority_rankings",
            type: "string",
          },
          { field: "top_targets", as: "top_targets", type: "string" },
          {
            field: "conversation_starters",
            as: "conversation_starters",
            type: "string",
          },
          {
            field: "existing_relationships",
            as: "existing_relationships",
            type: "string",
          },
          {
            field: "new_prospect_count",
            as: "new_prospect_count",
            type: "number",
          },
        ],
      ),
      sheetsAppend(
        "6",
        "Publish event brief",
        1240,
        "EventBrief!A:G",
        '[["{event_name}","{event_date}","{attendee_count}","{crm_match_count}","{new_prospect_count}","{top_targets}","{priority_rankings}"]]',
      ),
      emailNode(
        "7",
        "Send pre-event outreach",
        1480,
        "{sales_team_email}",
        "Event Brief: {event_name} ({event_date}) — {attendee_count} Attendees",
        "EVENT ATTENDEE BRIEF\nEvent: {event_name} | Date: {event_date}\nTotal Attendees: {attendee_count} | CRM Matches: {crm_match_count} | New Prospects: {new_prospect_count}\n\nTop Targets:\n{top_targets}\n\nConversation Starters:\n{conversation_starters}\n\nExisting Relationships:\n{existing_relationships}",
      ),
      sheetsAppend(
        "8",
        "Initialize meeting tracker",
        1720,
        "MeetingTracker!A:D",
        '[["{event_name}","{event_date}","{top_targets}","pending"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 303. OM18 PARTNERSHIP SCOUT (7 nodes)
  // ===================================================================
  "OM18 Partnership Scout": {
    summary:
      "Monthly ecosystem scrape, partnership normalization via runCode, AI gap/threat analysis, new partnership condition, partnership map update in Sheets, BD/product alert on Slack, and depth assessment.",
    nodes: [
      trigger("Schedule: Monthly partnership scan", "schedule", {
        schedule: "0 10 1 * *",
        howItStarts:
          "Runs on the 1st of every month to scan competitor ecosystem pages, detect new partnerships, and analyze strategic implications",
        setup:
          "Configure ScrapingBee key. Set competitor integrations/partnerships page URLs. Set spreadsheet_id for partnership map. Connect Slack webhook for BD team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Scrape competitor partner pages", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url={competitor_partners_url}&extract_rules={"partners":{"selector":".partner-card,.integration-item","type":"list","output":{"name":{"selector":".partner-name,h3"},"type":{"selector":".partner-type,.category"},"description":{"selector":".partner-desc,p"}}}}',
          headers: [],
          body: [],
          variables: [
            {
              name: "partners",
              customName: "current_partners",
              type: "string",
            },
          ],
        },
      }),
      n("3", "runCode", "Normalize and detect new partnerships", 520, {
        codeDetails: {
          language: "javascript",
          code: `const partners = JSON.parse(inputs.current_partners || '[]');
const prior = JSON.parse(inputs.prior_partners || '[]');
const priorNames = prior.map(p => (p.name || '').toLowerCase());
const newPartners = partners.filter(p => p.name && !priorNames.includes(p.name.toLowerCase()));
const categories = {};
partners.forEach(p => {
  const cat = p.type || 'General';
  categories[cat] = (categories[cat] || 0) + 1;
});
return { partner_categories: JSON.stringify(categories), new_partners: JSON.stringify(newPartners.map(p => p.name)), new_partner_count: newPartners.length, total_partners: partners.length, has_new_partnership: newPartners.length > 0 ? 'true' : 'false', scan_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "current_partners", type: "string" },
            { name: "prior_partners", type: "string" },
          ],
          outputVariables: [
            { name: "partner_categories", type: "string" },
            { name: "new_partners", type: "string" },
            { name: "new_partner_count", type: "number" },
            { name: "total_partners", type: "number" },
            { name: "has_new_partnership", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI gap/threat analysis",
        760,
        "You are a business development strategist. Analyze the competitor partnership ecosystem for: partnership gaps we can exploit, threats from strategic alliances, ecosystem power dynamics, and partnership depth. Respond JSON: {gap_opportunities: string, strategic_threats: string, ecosystem_dynamics: string, partnership_depth: string, recommended_outreach: string, threat_level: string}",
        "Partners: {current_partners}. Categories: {partner_categories}. New: {new_partners}. Total: {total_partners}.",
        [
          {
            field: "gap_opportunities",
            as: "gap_opportunities",
            type: "string",
          },
          {
            field: "strategic_threats",
            as: "strategic_threats",
            type: "string",
          },
          {
            field: "ecosystem_dynamics",
            as: "ecosystem_dynamics",
            type: "string",
          },
          {
            field: "partnership_depth",
            as: "partnership_depth",
            type: "string",
          },
          {
            field: "recommended_outreach",
            as: "recommended_outreach",
            type: "string",
          },
          { field: "threat_level", as: "threat_level", type: "string" },
        ],
      ),
      n("5", "condition", "New partnership detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_new_partnership", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Update partnership map",
        1240,
        "PartnershipMap!A:H",
        '[["{scan_date}","{total_partners}","{new_partner_count}","{new_partners}","{partner_categories}","{gap_opportunities}","{strategic_threats}","{threat_level}"]]',
      ),
      slackNode(
        "7",
        "BD and product alert",
        1480,
        "Partnership Scout ({scan_date})\nTotal: {total_partners} | New: {new_partner_count}\nNew Partners: {new_partners}\nGaps: {gap_opportunities}\nThreats: {strategic_threats}\nThreat Level: {threat_level}\nRecommended Outreach: {recommended_outreach}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 304. OM23 PH/HN WATCHER (7 nodes)
  // ===================================================================
  "OM23 PH/HN Watcher": {
    summary:
      "Frequent Product Hunt and Hacker News monitoring, AI relevance/threat scoring, direct competitor condition for immediate Slack alert, findings archive in Sheets, and response idea generation.",
    nodes: [
      trigger("Schedule: PH/HN scan (every 2 hours)", "schedule", {
        schedule: "0 */2 * * *",
        howItStarts:
          "Runs every 2 hours to monitor Product Hunt launches and Hacker News front page for competitive threats and market signals",
        setup:
          "Configure Product Hunt and HN API access. Set competitor keywords. Set spreadsheet_id for findings archive. Connect Slack webhook for immediate alerts.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull Product Hunt + HN posts", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://hn.algolia.com/api/v1/search?query={industry_keywords}&tags=story&hitsPerPage=20&numericFilters=created_at_i>{two_hours_ago_epoch}",
          headers: [],
          body: [],
          variables: [
            { name: "hits", customName: "hn_posts", type: "string" },
            { name: "nbHits", customName: "hn_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI score relevance and threat level",
        520,
        "You are a competitive intelligence analyst monitoring Product Hunt and Hacker News. Score each post for: relevance to our market (1-10), direct competitor threat (yes/no), innovation level, and market signal strength. Flag direct competitors for immediate alert. Respond JSON: {scored_posts: string, direct_competitors: string, is_direct_competitor: string, market_signals: string, innovation_highlights: string, threat_summary: string}",
        "HN posts: {hn_posts}. Count: {hn_count}. Industry: {industry_keywords}.",
        [
          { field: "scored_posts", as: "scored_posts", type: "string" },
          {
            field: "direct_competitors",
            as: "direct_competitors",
            type: "string",
          },
          {
            field: "is_direct_competitor",
            as: "is_direct_competitor",
            type: "string",
          },
          { field: "market_signals", as: "market_signals", type: "string" },
          {
            field: "innovation_highlights",
            as: "innovation_highlights",
            type: "string",
          },
          { field: "threat_summary", as: "threat_summary", type: "string" },
        ],
      ),
      n("4", "condition", "Direct competitor detected?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "is_direct_competitor",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      slackNode(
        "5",
        "Immediate competitor alert",
        1000,
        "DIRECT COMPETITOR ALERT (PH/HN)\n{direct_competitors}\nThreat: {threat_summary}\nMarket Signals: {market_signals}",
      ),
      sheetsAppend(
        "6",
        "Archive findings",
        1240,
        "PHHNWatch!A:G",
        '[["{scan_timestamp}","{hn_count}","{scored_posts}","{is_direct_competitor}","{direct_competitors}","{market_signals}","{innovation_highlights}"]]',
      ),
      aiNode(
        "7",
        "Generate response ideas",
        1480,
        "You are a marketing strategist. Given the competitive findings from PH/HN, suggest response ideas: counter-launches, content responses, community engagement tactics, and proactive moves. Respond JSON: {response_ideas: string, content_angles: string, community_tactics: string, proactive_moves: string}",
        "Threat: {threat_summary}. Competitors: {direct_competitors}. Signals: {market_signals}. Innovations: {innovation_highlights}.",
        [
          { field: "response_ideas", as: "response_ideas", type: "string" },
          { field: "content_angles", as: "content_angles", type: "string" },
          {
            field: "community_tactics",
            as: "community_tactics",
            type: "string",
          },
          { field: "proactive_moves", as: "proactive_moves", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 305. OM25 DAILY RESEARCH DIGEST (7 nodes)
  // ===================================================================
  "OM25 Daily Research Digest": {
    summary:
      "Daily aggregation of all competitive trackers, industry signal pull via SerpAPI, AI prioritization and synthesis, all-quiet condition, Slack highlights plus email digest, and archive to Sheets.",
    nodes: [
      trigger("Schedule: Daily research digest", "schedule", {
        schedule: "0 17 * * 1-5",
        howItStarts:
          "Runs at 5 PM on weekdays to aggregate all competitive intelligence trackers, pull industry signals, and synthesize a daily research digest",
        setup:
          "Configure SerpAPI key, Google Sheets access for all trackers, Slack webhook, and email distribution list.",
        inputFields: [],
      }),
      n("2", "hitApi", "Aggregate all tracker data from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{master_spreadsheet_id}/values:batchGet?ranges=HiringTracker!A:H&ranges=PricingWatch!A:G&ranges=FeatureRollout!A:G&ranges=ReviewSentiment!A:H&majorDimension=ROWS",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            {
              name: "valueRanges",
              customName: "all_tracker_data",
              type: "string",
            },
          ],
        },
      }),
      n("3", "hitApi", "Pull industry signals via SerpAPI", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={industry_keyword}+news&tbm=nws&num=15&tbs=qdr:d&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "news_results",
              customName: "industry_news",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "news_count",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "AI prioritize and synthesize",
        760,
        "You are a chief intelligence analyst. Synthesize today's competitive intelligence from all trackers and industry news into an executive digest. Prioritize by impact, identify cross-signal patterns, and flag anything requiring immediate attention. If nothing notable happened, mark as all-quiet. Respond JSON: {digest_headline: string, priority_items: string, cross_signal_patterns: string, immediate_attention: string, is_all_quiet: string, market_mood: string, daily_summary: string}",
        "Tracker data: {all_tracker_data}. Industry news: {industry_news}. News count: {news_count}.",
        [
          { field: "digest_headline", as: "digest_headline", type: "string" },
          { field: "priority_items", as: "priority_items", type: "string" },
          {
            field: "cross_signal_patterns",
            as: "cross_signal_patterns",
            type: "string",
          },
          {
            field: "immediate_attention",
            as: "immediate_attention",
            type: "string",
          },
          { field: "is_all_quiet", as: "is_all_quiet", type: "string" },
          { field: "market_mood", as: "market_mood", type: "string" },
          { field: "daily_summary", as: "daily_summary", type: "string" },
        ],
      ),
      n("5", "condition", "All quiet day?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_all_quiet", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack highlights + email digest",
        1240,
        "Daily Research Digest: {digest_headline}\nMarket Mood: {market_mood}\n\nPriority Items:\n{priority_items}\n\nCross-Signal Patterns:\n{cross_signal_patterns}\n\nImmediate Attention:\n{immediate_attention}",
      ),
      sheetsAppend(
        "7",
        "Archive digest",
        1480,
        "DailyDigest!A:G",
        '[["{scan_date}","{digest_headline}","{market_mood}","{priority_items}","{cross_signal_patterns}","{is_all_quiet}","{daily_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 306. PIPEDRIVE DAILY SUMMARY (7 nodes)
  // ===================================================================
  "Pipedrive Daily Summary": {
    summary:
      "Morning Pipedrive deals and activities pull, AI pipeline health analysis, stalled deal condition, dashboard update in Sheets, morning brief on Slack, and rep-level metrics.",
    nodes: [
      trigger("Schedule: Morning Pipedrive summary", "schedule", {
        schedule: "0 7 * * 1-5",
        howItStarts:
          "Runs at 7 AM on weekdays to pull Pipedrive deal and activity data, analyze pipeline health, and generate a morning sales brief",
        setup:
          "Configure Pipedrive API token. Set spreadsheet_id for pipeline dashboard. Connect Slack webhook for sales channel.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull Pipedrive deals and activities", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.pipedrive.com/v1/deals?status=open&sort=update_time DESC&limit=100&api_token={pipedrive_token}",
          headers: [],
          body: [],
          variables: [
            { name: "data", customName: "open_deals", type: "string" },
            {
              name: "additional_data.pagination.more_items_in_collection",
              customName: "has_more",
              type: "boolean",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze pipeline health",
        520,
        "You are a sales operations analyst. Analyze the open deals pipeline for: overall health, stage distribution, velocity, aging, stalled deals (no activity 7+ days), and forecast. Respond JSON: {health_grade: string, stage_distribution: string, avg_deal_age: number, stalled_deals: string, stalled_count: number, pipeline_value: string, forecast_summary: string, rep_performance: string}",
        "Open deals: {open_deals}.",
        [
          { field: "health_grade", as: "health_grade", type: "string" },
          {
            field: "stage_distribution",
            as: "stage_distribution",
            type: "string",
          },
          { field: "avg_deal_age", as: "avg_deal_age", type: "number" },
          { field: "stalled_deals", as: "stalled_deals", type: "string" },
          { field: "stalled_count", as: "stalled_count", type: "number" },
          { field: "pipeline_value", as: "pipeline_value", type: "string" },
          { field: "forecast_summary", as: "forecast_summary", type: "string" },
          { field: "rep_performance", as: "rep_performance", type: "string" },
        ],
      ),
      n("4", "condition", "Stalled deals detected?", 760, {
        conditionDetails: {
          conditions: [
            { field: "stalled_count", operator: "greater_than", value: "0" },
          ],
        },
      }),
      sheetsAppend(
        "5",
        "Update pipeline dashboard",
        1000,
        "PipedriveDash!A:H",
        '[["{report_date}","{health_grade}","{pipeline_value}","{avg_deal_age}","{stalled_count}","{stage_distribution}","{forecast_summary}","{rep_performance}"]]',
      ),
      slackNode(
        "6",
        "Morning sales brief on Slack",
        1240,
        "Morning Pipeline Brief\nHealth: {health_grade} | Value: {pipeline_value}\nAvg Deal Age: {avg_deal_age} days | Stalled: {stalled_count}\nStage Distribution: {stage_distribution}\nForecast: {forecast_summary}\nStalled Deals: {stalled_deals}",
      ),
      sheetsAppend(
        "7",
        "Log rep-level metrics",
        1480,
        "RepMetrics!A:C",
        '[["{report_date}","{rep_performance}","{health_grade}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("6", "5"),
      e("5", "7"),
    ],
  },

  // ===================================================================
  // 307. REAL-TIME KPI ALERTING SYSTEM (8 nodes)
  // ===================================================================
  "Real-Time KPI Alerting System": {
    summary:
      "15-minute multi-source KPI monitoring with threshold comparison via runCode, severity condition routing, dedup/persistence check, Slack warning alert, SMS critical alert via Twilio, and acknowledgment logging.",
    nodes: [
      trigger("Schedule: KPI check every 15 minutes", "schedule", {
        schedule: "*/15 * * * *",
        howItStarts:
          "Runs every 15 minutes to check critical KPIs across all sources, compare against thresholds, and alert via Slack or SMS based on severity",
        setup:
          "Configure GA4, Stripe, and app monitoring API credentials. Set Twilio account for SMS alerts. Set spreadsheet_id for alert log. Connect Slack webhook.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull multi-source KPIs", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runRealtimeReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "metrics",
              value: '[{"name":"activeUsers"},{"name":"conversions"}]',
            },
          ],
          variables: [
            {
              name: "rows[0].metricValues[0].value",
              customName: "realtime_users",
              type: "number",
            },
            {
              name: "rows[0].metricValues[1].value",
              customName: "realtime_conversions",
              type: "number",
            },
          ],
        },
      }),
      n("3", "runCode", "Compare against thresholds", 520, {
        codeDetails: {
          language: "javascript",
          code: `const users = Number(inputs.realtime_users) || 0;
const conversions = Number(inputs.realtime_conversions) || 0;
const alerts = [];
let severity = 'normal';
if (users < 5) { alerts.push('Active users critically low: ' + users); severity = 'critical'; }
else if (users < 20) { alerts.push('Active users below warning: ' + users); severity = 'warning'; }
if (conversions === 0 && users > 10) { alerts.push('Zero conversions with active traffic'); severity = 'critical'; }
const hasAlert = alerts.length > 0 ? 'true' : 'false';
return { alert_messages: alerts.join('; ') || 'All KPIs normal', severity, has_alert: hasAlert, check_time: new Date().toISOString(), kpi_snapshot: 'Users: ' + users + ' | Conversions: ' + conversions };`,
          inputVariables: [
            { name: "realtime_users", type: "number" },
            { name: "realtime_conversions", type: "number" },
          ],
          outputVariables: [
            { name: "alert_messages", type: "string" },
            { name: "severity", type: "string" },
            { name: "has_alert", type: "string" },
            { name: "check_time", type: "string" },
            { name: "kpi_snapshot", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Alert triggered?", 760, {
        conditionDetails: {
          conditions: [
            { field: "has_alert", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "runCode", "Dedup and persistence check", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Check if this is a repeat alert (would normally query a store)
const severity = inputs.severity;
const alertMsg = inputs.alert_messages;
const isCritical = severity === 'critical' ? 'true' : 'false';
const alertId = 'alert_' + Date.now();
return { alert_id: alertId, is_critical: isCritical, deduplicated: 'false', escalation_needed: isCritical };`,
          inputVariables: [
            { name: "severity", type: "string" },
            { name: "alert_messages", type: "string" },
          ],
          outputVariables: [
            { name: "alert_id", type: "string" },
            { name: "is_critical", type: "string" },
            { name: "deduplicated", type: "string" },
            { name: "escalation_needed", type: "string" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack warning alert",
        1240,
        "KPI Alert [{severity}] (ID: {alert_id})\n{alert_messages}\nSnapshot: {kpi_snapshot}\nTime: {check_time}",
      ),
      n(
        "7",
        "sendSMS",
        "SMS critical alert via Twilio",
        1480,
        {
          sendSMSDetails: {
            to: "{oncall_phone}",
            body: "CRITICAL KPI ALERT: {alert_messages}. Snapshot: {kpi_snapshot}. Time: {check_time}. ID: {alert_id}",
          },
        },
        500,
      ),
      sheetsAppend(
        "8",
        "Log alert acknowledgment",
        1720,
        "KPIAlerts!A:F",
        '[["{check_time}","{alert_id}","{severity}","{alert_messages}","{kpi_snapshot}","{deduplicated}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("5", "7"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 308. REPUTATION DASHBOARD BUILDER (8 nodes)
  // ===================================================================
  "Reputation Dashboard Builder": {
    summary:
      "Weekly multi-platform reputation monitoring. Pulls review scores, NPS/CSAT data, social sentiment, generates AI composite health score, updates dashboard in Sheets, sends leadership summary email, and archives verbatim feedback.",
    nodes: [
      trigger("Schedule: Weekly reputation scan", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday at 9 AM to aggregate reputation signals from review platforms, NPS/CSAT surveys, and social media into a composite health score",
        setup:
          "Configure G2/Trustpilot API for reviews, survey tool API for NPS/CSAT, and social listening API. Set spreadsheet_id for dashboard. Add leadership email.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull review platform scores", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://data.g2.com/api/v1/products/{product_g2_id}",
          headers: [
            { key: "Authorization", value: "Token token={g2_api_token}" },
            { key: "Content-Type", value: "application/vnd.api+json" },
          ],
          body: [],
          variables: [
            {
              name: "data.attributes.star_rating",
              customName: "g2_rating",
              type: "string",
            },
            {
              name: "data.attributes.review_count",
              customName: "g2_review_count",
              type: "number",
            },
          ],
        },
      }),
      n("3", "hitApi", "Pull NPS and CSAT data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.delighted.com/v1/metrics.json",
          headers: [
            { key: "Authorization", value: "Basic {delighted_api_key_base64}" },
          ],
          body: [],
          variables: [
            { name: "nps", customName: "nps_score", type: "number" },
            { name: "promoter_count", customName: "promoters", type: "number" },
            {
              name: "detractor_count",
              customName: "detractors",
              type: "number",
            },
            {
              name: "response_count",
              customName: "nps_responses",
              type: "number",
            },
          ],
        },
      }),
      n("4", "hitApi", "Pull social sentiment data", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.brandwatch.com/projects/{project_id}/data/mentions?queryId={query_id}&startDate={week_ago_date}&endDate={today_date}&pageSize=50",
          headers: [
            { key: "Authorization", value: "Bearer {brandwatch_token}" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "social_mentions", type: "string" },
            {
              name: "resultsTotal",
              customName: "mention_count",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "5",
        "AI compute composite health score",
        1000,
        "You are a brand reputation analyst. Given review scores, NPS/CSAT data, and social mentions, compute a composite reputation health score (0-100), identify the strongest and weakest pillars, extract verbatim quotes that matter, and forecast reputation trajectory. Respond JSON: {composite_score: number, strongest_pillar: string, weakest_pillar: string, key_verbatims: string, trajectory: string, risk_factors: string, executive_summary: string}",
        "G2 Rating: {g2_rating} ({g2_review_count} reviews). NPS: {nps_score} (promoters: {promoters}, detractors: {detractors}, responses: {nps_responses}). Social mentions: {mention_count}. Mentions: {social_mentions}.",
        [
          { field: "composite_score", as: "composite_score", type: "number" },
          { field: "strongest_pillar", as: "strongest_pillar", type: "string" },
          { field: "weakest_pillar", as: "weakest_pillar", type: "string" },
          { field: "key_verbatims", as: "key_verbatims", type: "string" },
          { field: "trajectory", as: "trajectory", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
        ],
      ),
      sheetsAppend(
        "6",
        "Update reputation dashboard",
        1240,
        "ReputationDash!A:I",
        '[["{scan_date}","{composite_score}","{g2_rating}","{nps_score}","{mention_count}","{strongest_pillar}","{weakest_pillar}","{trajectory}","{risk_factors}"]]',
      ),
      emailNode(
        "7",
        "Leadership reputation summary",
        1480,
        "{leadership_email}",
        "Weekly Reputation Report — Score: {composite_score}/100 | Trajectory: {trajectory}",
        "REPUTATION HEALTH REPORT\n\nComposite Score: {composite_score}/100\nTrajectory: {trajectory}\n\nPillars:\n- Strongest: {strongest_pillar}\n- Weakest: {weakest_pillar}\n\nG2: {g2_rating} ({g2_review_count} reviews)\nNPS: {nps_score}\nSocial Mentions: {mention_count}\n\nRisk Factors: {risk_factors}\n\n{executive_summary}",
      ),
      sheetsAppend(
        "8",
        "Archive verbatim feedback",
        1720,
        "VerbatimArchive!A:C",
        '[["{scan_date}","{key_verbatims}","{composite_score}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 309. SEARCH TERM WASTE FINDER (7 nodes)
  // ===================================================================
  "Search Term Waste Finder": {
    summary:
      "Monthly search term analysis, AI waste categorization, spend quantification via runCode, budget tolerance condition, waste report in Sheets, paid media notification on Slack, and savings estimate.",
    nodes: [
      trigger("Schedule: Monthly waste analysis", "schedule", {
        schedule: "0 10 1 * *",
        howItStarts:
          "Runs on the 1st of every month to analyze search term spend, identify waste categories, and estimate potential savings",
        setup:
          "Configure Google Ads API credentials. Set monthly waste tolerance threshold. Set spreadsheet_id for waste report. Connect Slack webhook for paid media.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull monthly search term report", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT search_term_view.search_term, metrics.cost_micros, metrics.conversions, metrics.clicks, metrics.impressions FROM search_term_view WHERE segments.date DURING LAST_30_DAYS AND metrics.cost_micros > 100000 ORDER BY metrics.cost_micros DESC LIMIT 500",
            },
          ],
          variables: [
            { name: "results", customName: "search_terms", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI categorize waste",
        520,
        "You are a paid search expert. Analyze search terms and categorize wasted spend: irrelevant queries, low-intent informational, competitor brand (if unwanted), misspelling matches, geographic mismatches, and over-broad matches. Estimate waste percentage per category. Respond JSON: {waste_categories: string, total_waste_terms: number, waste_by_category: string, highest_waste_category: string, cleanup_recommendations: string}",
        "Search terms with spend: {search_terms}.",
        [
          { field: "waste_categories", as: "waste_categories", type: "string" },
          {
            field: "total_waste_terms",
            as: "total_waste_terms",
            type: "number",
          },
          {
            field: "waste_by_category",
            as: "waste_by_category",
            type: "string",
          },
          {
            field: "highest_waste_category",
            as: "highest_waste_category",
            type: "string",
          },
          {
            field: "cleanup_recommendations",
            as: "cleanup_recommendations",
            type: "string",
          },
        ],
      ),
      n("4", "runCode", "Quantify wasted spend", 760, {
        codeDetails: {
          language: "javascript",
          code: `const wasteTerms = Number(inputs.total_waste_terms) || 0;
// Average waste per term (simplified estimation)
const avgWastePerTerm = 8.50;
const monthlyWaste = (wasteTerms * avgWastePerTerm).toFixed(2);
const annualWaste = (Number(monthlyWaste) * 12).toFixed(2);
const exceedsBudgetTolerance = Number(monthlyWaste) > 500 ? 'true' : 'false';
return { monthly_waste: monthlyWaste, annual_waste_estimate: annualWaste, exceeds_tolerance: exceedsBudgetTolerance, waste_per_term: avgWastePerTerm.toFixed(2), analysis_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [{ name: "total_waste_terms", type: "number" }],
          outputVariables: [
            { name: "monthly_waste", type: "string" },
            { name: "annual_waste_estimate", type: "string" },
            { name: "exceeds_tolerance", type: "string" },
            { name: "waste_per_term", type: "string" },
            { name: "analysis_date", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Exceeds budget tolerance?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "exceeds_tolerance", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Publish waste report",
        1240,
        "SearchTermWaste!A:H",
        '[["{analysis_date}","{total_waste_terms}","{monthly_waste}","{annual_waste_estimate}","{highest_waste_category}","{waste_by_category}","{waste_categories}","{cleanup_recommendations}"]]',
      ),
      slackNode(
        "7",
        "Paid media notification + savings estimate",
        1480,
        "Search Term Waste Report ({analysis_date})\nWaste Terms: {total_waste_terms}\nMonthly Waste: ${monthly_waste} | Annual Estimate: ${annual_waste_estimate}\nHighest Category: {highest_waste_category}\nCategories: {waste_by_category}\nRecommendations: {cleanup_recommendations}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 310. TOP-TIME SCHEDULING OPTIMIZER (7 nodes)
  // ===================================================================
  "Top-Time Scheduling Optimizer": {
    summary:
      "Weekly analysis of historical post performance, AI time-window analysis, scheduling conflict check via runCode, recommended times published to Sheets, social team notification on Slack, and sample size validation.",
    nodes: [
      trigger("Schedule: Weekly time optimization", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Runs every Monday at 8 AM to analyze historical social media performance by time slot and recommend optimal posting windows",
        setup:
          "Configure social media analytics API (e.g., Buffer, Sprout Social). Set spreadsheet_id for recommended times. Connect Slack webhook for social team.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull historical post performance", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bufferapp.com/1/profiles/{profile_id}/updates/sent.json?count=100&access_token={buffer_token}",
          headers: [],
          body: [],
          variables: [
            { name: "updates", customName: "post_history", type: "string" },
            { name: "total", customName: "post_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze optimal time windows",
        520,
        "You are a social media analytics expert. Analyze historical post performance data to identify the optimal posting times by day of week and hour. Consider engagement rates, reach, and click-through rates by time slot. Account for platform-specific patterns. Respond JSON: {optimal_times: string, day_rankings: string, peak_engagement_windows: string, avoid_times: string, platform_specific_notes: string, confidence_level: string}",
        "Post history: {post_history}. Total posts analyzed: {post_count}.",
        [
          { field: "optimal_times", as: "optimal_times", type: "string" },
          { field: "day_rankings", as: "day_rankings", type: "string" },
          {
            field: "peak_engagement_windows",
            as: "peak_engagement_windows",
            type: "string",
          },
          { field: "avoid_times", as: "avoid_times", type: "string" },
          {
            field: "platform_specific_notes",
            as: "platform_notes",
            type: "string",
          },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),
      n("4", "runCode", "Check scheduling conflicts and validate sample", 760, {
        codeDetails: {
          language: "javascript",
          code: `const postCount = Number(inputs.post_count) || 0;
const minSample = 30;
const sampleValid = postCount >= minSample ? 'true' : 'false';
const sampleNote = postCount < minSample ? 'Warning: Only ' + postCount + ' posts analyzed. Need ' + minSample + '+ for reliable recommendations.' : postCount + ' posts analyzed — sufficient sample size.';
const conflictCheck = 'No scheduling conflicts detected';
return { sample_valid: sampleValid, sample_note: sampleNote, conflict_check: conflictCheck, posts_analyzed: postCount, analysis_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [{ name: "post_count", type: "number" }],
          outputVariables: [
            { name: "sample_valid", type: "string" },
            { name: "sample_note", type: "string" },
            { name: "conflict_check", type: "string" },
            { name: "posts_analyzed", type: "number" },
            { name: "analysis_date", type: "string" },
          ],
        },
      }),
      sheetsAppend(
        "5",
        "Publish recommended times",
        1000,
        "PostingSchedule!A:G",
        '[["{analysis_date}","{optimal_times}","{day_rankings}","{peak_engagement_windows}","{avoid_times}","{confidence_level}","{sample_note}"]]',
      ),
      slackNode(
        "6",
        "Notify social team",
        1240,
        "Posting Time Optimization ({analysis_date})\nOptimal Times: {optimal_times}\nDay Rankings: {day_rankings}\nPeak Windows: {peak_engagement_windows}\nAvoid: {avoid_times}\nConfidence: {confidence_level}\nSample: {sample_note}",
      ),
      sheetsAppend(
        "7",
        "Log sample validation",
        1480,
        "SampleValidation!A:D",
        '[["{analysis_date}","{posts_analyzed}","{sample_valid}","{sample_note}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 311. WEEKLY AD PERFORMANCE DIGEST (7 nodes)
  // ===================================================================
  "Weekly Ad Performance Digest": {
    summary:
      "Weekly pull from Google, Meta, and LinkedIn ads, cross-platform normalization via runCode, AI efficiency analysis, anomaly condition routing, dashboard update in Sheets, and budget recommendation digest email.",
    nodes: [
      trigger("Schedule: Weekly ad performance digest", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday at 9 AM to aggregate ad performance across Google, Meta, and LinkedIn, normalize metrics, and generate efficiency recommendations",
        setup:
          "Configure Google Ads, Meta Marketing, and LinkedIn Campaign Manager API credentials. Set spreadsheet_id for dashboard. Add marketing leadership email.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull Google + Meta + LinkedIn ad data", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, metrics.impressions, metrics.clicks, metrics.cost_micros, metrics.conversions, metrics.conversions_value FROM campaign WHERE segments.date DURING LAST_7_DAYS",
            },
          ],
          variables: [
            { name: "results", customName: "google_ads_data", type: "string" },
          ],
        },
      }),
      n("3", "runCode", "Cross-platform normalization", 520, {
        codeDetails: {
          language: "javascript",
          code: `const googleRaw = inputs.google_ads_data || '[]';
const metaRaw = inputs.meta_ads_data || '[]';
const linkedinRaw = inputs.linkedin_ads_data || '[]';
// Parse and normalize
let totalSpend = 0, totalClicks = 0, totalConversions = 0, totalImpressions = 0;
try {
  const google = JSON.parse(googleRaw);
  google.forEach(r => {
    const m = r.metrics || {};
    totalSpend += (Number(m.cost_micros) || 0) / 1000000;
    totalClicks += Number(m.clicks) || 0;
    totalConversions += Number(m.conversions) || 0;
    totalImpressions += Number(m.impressions) || 0;
  });
} catch(e) {}
const cpc = totalClicks > 0 ? (totalSpend / totalClicks).toFixed(2) : '0';
const cpa = totalConversions > 0 ? (totalSpend / totalConversions).toFixed(2) : '0';
const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0';
const hasAnomaly = (Number(cpa) > 100 || Number(ctr) < 0.5) ? 'true' : 'false';
return { total_spend: totalSpend.toFixed(2), total_clicks: totalClicks, total_conversions: totalConversions, total_impressions: totalImpressions, avg_cpc: cpc, avg_cpa: cpa, avg_ctr: ctr, has_anomaly: hasAnomaly, period: 'last_7_days' };`,
          inputVariables: [
            { name: "google_ads_data", type: "string" },
            { name: "meta_ads_data", type: "string" },
            { name: "linkedin_ads_data", type: "string" },
          ],
          outputVariables: [
            { name: "total_spend", type: "string" },
            { name: "total_clicks", type: "number" },
            { name: "total_conversions", type: "number" },
            { name: "total_impressions", type: "number" },
            { name: "avg_cpc", type: "string" },
            { name: "avg_cpa", type: "string" },
            { name: "avg_ctr", type: "string" },
            { name: "has_anomaly", type: "string" },
            { name: "period", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI efficiency analysis",
        760,
        "You are a performance marketing analyst. Analyze cross-platform ad performance metrics for efficiency, identify underperforming campaigns, recommend budget reallocation, and flag anomalies. Respond JSON: {efficiency_grade: string, platform_comparison: string, underperformers: string, budget_recommendations: string, anomaly_details: string, key_wins: string}",
        "Period: {period}. Spend: ${total_spend}. Clicks: {total_clicks}. Conversions: {total_conversions}. Impressions: {total_impressions}. CPC: ${avg_cpc}. CPA: ${avg_cpa}. CTR: {avg_ctr}%.",
        [
          { field: "efficiency_grade", as: "efficiency_grade", type: "string" },
          {
            field: "platform_comparison",
            as: "platform_comparison",
            type: "string",
          },
          { field: "underperformers", as: "underperformers", type: "string" },
          {
            field: "budget_recommendations",
            as: "budget_recommendations",
            type: "string",
          },
          { field: "anomaly_details", as: "anomaly_details", type: "string" },
          { field: "key_wins", as: "key_wins", type: "string" },
        ],
      ),
      n("5", "condition", "Performance anomaly detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_anomaly", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Update ad performance dashboard",
        1240,
        "AdPerformance!A:I",
        '[["{period}","{total_spend}","{total_clicks}","{total_conversions}","{avg_cpc}","{avg_cpa}","{avg_ctr}","{efficiency_grade}","{budget_recommendations}"]]',
      ),
      emailNode(
        "7",
        "Budget recommendation digest",
        1480,
        "{marketing_leadership_email}",
        "Weekly Ad Performance — Grade: {efficiency_grade} | Spend: ${total_spend}",
        "WEEKLY AD PERFORMANCE DIGEST\nPeriod: {period}\n\nMetrics:\n- Spend: ${total_spend}\n- Clicks: {total_clicks} | Impressions: {total_impressions}\n- Conversions: {total_conversions}\n- CPC: ${avg_cpc} | CPA: ${avg_cpa} | CTR: {avg_ctr}%\n\nGrade: {efficiency_grade}\n\nPlatform Comparison:\n{platform_comparison}\n\nKey Wins: {key_wins}\nUnderperformers: {underperformers}\n\nBudget Recommendations:\n{budget_recommendations}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 312. WEEKLY SOCIAL MEDIA DIGEST (7 nodes)
  // ===================================================================
  "Weekly Social Media Digest": {
    summary:
      "Weekly multi-platform social metrics pull, growth/engagement normalization via runCode, AI content theme analysis, milestone condition, social dashboard update in Sheets, and marketing digest on Slack.",
    nodes: [
      trigger("Schedule: Weekly social digest", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday at 9 AM to aggregate social media metrics across all platforms, analyze content themes, and celebrate milestones",
        setup:
          "Configure social media API credentials (Buffer, Hootsuite, or native APIs). Set spreadsheet_id for social dashboard. Connect Slack webhook for marketing.",
        inputFields: [],
      }),
      n("2", "hitApi", "Pull multi-platform social metrics", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bufferapp.com/1/profiles/{profile_id}/analytics.json?start_date={week_ago_date}&end_date={today_date}&access_token={buffer_token}",
          headers: [],
          body: [],
          variables: [
            {
              name: "total_followers",
              customName: "total_followers",
              type: "number",
            },
            {
              name: "total_updates_published",
              customName: "posts_published",
              type: "number",
            },
            {
              name: "total_engagement",
              customName: "total_engagement",
              type: "number",
            },
            {
              name: "total_impressions",
              customName: "total_impressions",
              type: "number",
            },
          ],
        },
      }),
      n("3", "runCode", "Normalize growth and engagement", 520, {
        codeDetails: {
          language: "javascript",
          code: `const followers = Number(inputs.total_followers) || 0;
const posts = Number(inputs.posts_published) || 0;
const engagement = Number(inputs.total_engagement) || 0;
const impressions = Number(inputs.total_impressions) || 0;
const priorFollowers = Number(inputs.prior_followers) || followers;
const followerGrowth = priorFollowers > 0 ? (((followers - priorFollowers) / priorFollowers) * 100).toFixed(2) : '0';
const engagementRate = impressions > 0 ? ((engagement / impressions) * 100).toFixed(2) : '0';
const avgEngPerPost = posts > 0 ? (engagement / posts).toFixed(0) : '0';
// Milestones: round number followers, growth spike
const hasMilestone = (followers % 1000 < 50 || Number(followerGrowth) > 5) ? 'true' : 'false';
return { follower_growth_pct: followerGrowth, engagement_rate: engagementRate, avg_engagement_per_post: avgEngPerPost, has_milestone: hasMilestone, report_period: 'last_7_days', report_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "total_followers", type: "number" },
            { name: "posts_published", type: "number" },
            { name: "total_engagement", type: "number" },
            { name: "total_impressions", type: "number" },
            { name: "prior_followers", type: "number" },
          ],
          outputVariables: [
            { name: "follower_growth_pct", type: "string" },
            { name: "engagement_rate", type: "string" },
            { name: "avg_engagement_per_post", type: "string" },
            { name: "has_milestone", type: "string" },
            { name: "report_period", type: "string" },
            { name: "report_date", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI content theme analysis",
        760,
        "You are a social media strategist. Analyze the weekly social performance metrics and identify: top-performing content themes, audience engagement patterns, growth drivers, content gaps, and recommendations for next week. Respond JSON: {top_themes: string, audience_patterns: string, growth_drivers: string, content_gaps: string, next_week_recommendations: string, performance_summary: string}",
        "Followers: {total_followers} (+{follower_growth_pct}%). Posts: {posts_published}. Engagement: {total_engagement} ({engagement_rate}% rate). Impressions: {total_impressions}. Avg engagement/post: {avg_engagement_per_post}.",
        [
          { field: "top_themes", as: "top_themes", type: "string" },
          {
            field: "audience_patterns",
            as: "audience_patterns",
            type: "string",
          },
          { field: "growth_drivers", as: "growth_drivers", type: "string" },
          { field: "content_gaps", as: "content_gaps", type: "string" },
          {
            field: "next_week_recommendations",
            as: "next_week_recommendations",
            type: "string",
          },
          {
            field: "performance_summary",
            as: "performance_summary",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "Milestone reached?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_milestone", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsAppend(
        "6",
        "Update social dashboard",
        1240,
        "SocialDashboard!A:I",
        '[["{report_date}","{total_followers}","{follower_growth_pct}","{posts_published}","{total_engagement}","{engagement_rate}","{total_impressions}","{top_themes}","{performance_summary}"]]',
      ),
      slackNode(
        "7",
        "Marketing digest on Slack",
        1480,
        "Weekly Social Digest ({report_date})\nFollowers: {total_followers} ({follower_growth_pct}% growth)\nPosts: {posts_published} | Engagement: {total_engagement} ({engagement_rate}%)\nImpressions: {total_impressions} | Avg Eng/Post: {avg_engagement_per_post}\n\nTop Themes: {top_themes}\nGrowth Drivers: {growth_drivers}\nContent Gaps: {content_gaps}\nNext Week: {next_week_recommendations}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
    ],
  },
};
